import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const stockholmPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-stockholm",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Stockholm – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Stockholm. Vi hämtar möbler, vitvaror och skräp från alla stadsdelar – Södermalm, Kungsholmen, Bromma och fler. Fast pris, ofta samma dag.",
      h1: "Bortforsling Stockholm",
    },

    hero: {
      heading: "Bortforsling i Stockholm",
      intro:
        "Boka bortforsling i Stockholm – vi hämtar möbler, vitvaror och skrymmande föremål från alla stadsdelar och kranskommuner. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Stockholm",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Stockholm – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Stockholm är en stad med trånga gator, höga hus och begränsad parkering – det gör bortforsling till en utmaning om man ska försöka klara det själv. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress, oavsett om du bor på Södermalm, i Bromma, på Kungsholmen eller i någon av Stockholms kranskommuner som Nacka, Solna eller Huddinge.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet eller ditt hus, lastar i bilen och kör till närmaste återvinningscentral. I Stockholmsområdet arbetar vi främst med Bromma återvinningscentral, Lövsta ÅVC och Högdalens ÅVC – men vi anpassar oss alltid efter var du befinner dig. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nBortforsling i Stockholm innebär ofta logistiska utmaningar som trånga trapphus i sekelskifteshus på Vasastan, smala hissar i funkisbyggnader på Gärdet, eller begränsade infarter i innerstaden. Våra medarbetare är vana vid dessa förutsättningar och har utrustningen som krävs – bärbälten, skyddsfiltar och lastramper. Vi skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Stockholm",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Om du bor i lägenhet, kontrollera om det finns en lastzon eller innergård som vi kan använda för lastning – det sparar tid jämfört med att parkera på gatan. I centrala Stockholm kan parkeringssituationen vara utmanande, så om du har tillgång till en garageinfart eller innergård, meddela oss det vid bokningen.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen eller i ett rum. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant kan du lämna gratis på Stockholms återvinningscentraler.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi täcker i Stockholm",
          content:
            "Vi utför bortforsling i hela Stockholmsregionen – från innerstaden till ytterstaden och kranskommunerna. Oavsett om du bor i en etta på Södermalm, en villa i Bromma, en radhuslänga i Tyresö eller ett kontor i Kista kan vi vara på plats, ofta redan samma dag.\n\nI innerstaden (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen) har vi stor erfarenhet av de specifika utmaningar som äldre byggnader medför – trånga spiraltrappor, små hissar och begränsade möjligheter att parkera nära porten. Vi planerar alltid rutten och logistiken i förväg för att minimera tiden det tar.\n\nI söderförort (Farsta, Skarpnäck, Enskede, Bandhagen, Rågsved) och västerort (Vällingby, Hässelby, Spånga, Bromma) når vi er snabbt tack vare god tillgång till ringvägar och motorleder. Samma gäller för kranskommuner som Nacka, Huddinge, Sollentuna, Järfälla och Täby – vi tar oss dit utan problem och utan extra avgifter för området.",
        },
        {
          heading: "Återvinning och miljöansvar i Stockholm",
          content:
            "En viktig del av vår bortforsling i Stockholm är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i Stockholmsområdet. De stationer vi använder mest är:\n\nBromma återvinningscentral – centralt belägen och lättillgänglig, perfekt för uppdrag i västerort och innerstaden. Lövsta ÅVC – en av Stockholms största stationer, med kapacitet för de flesta materialtyper. Högdalens ÅVC – vår huvudstation för uppdrag i söderort.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen. Du kan dessutom ha rätt till RUT-avdrag om föremålen transporteras till återbruk.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Stockholms stadsdelar, trafikmönster och återvinningscentraler utan och innan. Det gör att vi kan planera effektiva rutter och minimera restiden – vilket håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för innerstaden, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Stockholm?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Stockholm?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Stockholm?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och kan ofta vara på plats redan samma dag. Vid akuta behov kan vi ibland komma inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Stockholm?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa?",
        answer:
          "Absolut. Vi är vana vid Stockholms trånga trapphus, speciellt i äldre sekelskifteshus på Södermalm, Vasastan och Östermalm. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Stockholm försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Hur hanterar ni återvinning efter bortforsling i Stockholm?",
        answer:
          "Vi sorterar allt material och kör det till godkända återvinningscentraler som Bromma, Lövsta och Högdalens ÅVC. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen. Du kan ha rätt till RUT-avdrag vid transport till återbruk.",
      },
      {
        question: "Kan jag boka bortforsling i Stockholm på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Stockholm?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid de trånga passagerna som är vanliga i Stockholms fastigheter.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Stockholm?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet och ditt förråd på en annan adress.",
      },
      {
        question: "Vilka stadsdelar i Stockholm täcker ni?",
        answer:
          "Vi utför bortforsling i hela Stockholm – innerstaden (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), söderort (Farsta, Enskede, Bandhagen), västerort (Bromma, Vällingby, Hässelby) och alla övriga stadsdelar.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Stockholm?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant kan du lämna gratis på Stockholms återvinningscentraler, till exempel Bromma eller Lövsta ÅVC.",
      },
      {
        question: "Behöver jag vara hemma vid bortforslingen?",
        answer:
          "Vi rekommenderar att du är hemma eller att någon annan vuxen finns på plats för att visa vad som ska hämtas. Det går också att lämna instruktioner om föremålen står lättillgängligt, till exempel i ett öppet förråd eller utanför porten.",
      },
      {
        question: "Vilka kommuner runt Stockholm utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Stockholm i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Stockholm kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus och smala hissar – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Stockholm idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Stockholmsregionen.",
    },

    en: {
      slug: "junk-removal-stockholm",
      meta: {
        title: "Junk Removal Stockholm – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Stockholm. We pick up furniture, appliances and bulky items from all neighborhoods. Fixed price, insured, often same day. Book today!",
        h1: "Junk Removal Stockholm",
      },
      hero: {
        heading: "Junk Removal in Stockholm",
        intro:
          "Book junk removal in Stockholm – we collect furniture, appliances and bulky items from all neighborhoods and surrounding municipalities. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
      },
      itemsSection: {
        heading: "Items we remove in Stockholm",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Stockholm – fast, hassle-free and responsible",
        mainText:
          "Stockholm is a city with narrow streets, tall buildings and limited parking – making junk removal a real challenge if you try to do it yourself. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address, whether you live in Södermalm, Bromma, Kungsholmen or any of Stockholm's surrounding municipalities like Nacka, Solna or Huddinge.\n\nOur team handles the entire chain: we carry items out of your apartment or house, load them into the van and drive to the nearest recycling centre. In the Stockholm area, we mainly use Bromma recycling centre, Lövsta and Högdalen – but we always adapt to where you are located. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nJunk removal in Stockholm often involves logistical challenges like narrow stairways in turn-of-the-century buildings in Vasastan, small elevators in functionalist buildings on Gärdet, or limited access in the inner city. Our workers are accustomed to these conditions and have the right equipment – carrying belts, protective blankets and loading ramps. We protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Stockholm",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in an apartment, check if there is a loading zone or courtyard we can use – it saves time compared to parking on the street. In central Stockholm, parking can be challenging, so if you have access to a garage entrance or courtyard, let us know when booking.\n\nGather the items in one place if possible, for example in the hallway or one room. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but you can drop those off for free at Stockholm's recycling centres.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover in Stockholm",
            content:
              "We perform junk removal across the entire Stockholm region – from the inner city to the suburbs and surrounding municipalities. Whether you live in a studio apartment in Södermalm, a villa in Bromma, a townhouse in Tyresö or an office in Kista, we can be on site, often the same day.\n\nIn the inner city (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen) we have extensive experience with the specific challenges that older buildings present – narrow spiral staircases, small elevators and limited options for parking near the entrance. We always plan the route and logistics in advance to minimise time.\n\nIn the southern suburbs (Farsta, Skarpnäck, Enskede, Bandhagen, Rågsved) and western suburbs (Vällingby, Hässelby, Spånga, Bromma) we reach you quickly thanks to good access to ring roads and motorways. The same goes for surrounding municipalities like Nacka, Huddinge, Sollentuna, Järfälla and Täby – we get there without issue and without extra charges for the area.",
          },
          {
            heading: "Recycling and environmental responsibility in Stockholm",
            content:
              "An important part of our junk removal in Stockholm is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the Stockholm area. The stations we use most are:\n\nBromma recycling centre – centrally located and easily accessible, perfect for jobs in the western suburbs and inner city. Lövsta recycling centre – one of Stockholm's largest stations, with capacity for most material types. Högdalen recycling centre – our main station for jobs in the southern suburbs.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill. You may also be entitled to a RUT tax deduction when items are transported for reuse.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Stockholm's neighborhoods, traffic patterns and recycling centres inside and out. This allows us to plan efficient routes and minimise travel time – which keeps the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for the inner city, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, walls and belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Stockholm cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Stockholm?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Stockholm?",
          answer:
            "We have vans and staff across the Stockholm region and can often be on site the same day. For urgent needs, we can sometimes arrive within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Stockholm?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal.",
        },
        {
          question: "Can you carry items down from an apartment via stairs?",
          answer:
            "Absolutely. We are used to Stockholm's narrow stairways, especially in older buildings in Södermalm, Vasastan and Östermalm. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get them through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Stockholm insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "How do you handle recycling after junk removal in Stockholm?",
          answer:
            "We sort all materials and drive them to approved recycling centres like Bromma, Lövsta and Högdalen. Items that can be reused are donated to Myrorna or Stadsmissionen. You may be entitled to a RUT tax deduction for transport to reuse.",
        },
        {
          question: "Can I book junk removal in Stockholm on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Stockholm?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to the tight passages common in Stockholm's buildings.",
        },
        {
          question: "Can you make multiple stops during junk removal in Stockholm?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment and a storage unit at a different address, for example.",
        },
        {
          question: "Which neighborhoods in Stockholm do you cover?",
          answer:
            "We perform junk removal across all of Stockholm – the inner city (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), southern suburbs (Farsta, Enskede, Bandhagen), western suburbs (Bromma, Vällingby, Hässelby) and all other neighborhoods.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Stockholm?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. You can drop those off for free at Stockholm's recycling centres, such as Bromma or Lövsta.",
        },
        {
          question: "Do I need to be home during the junk removal?",
          answer:
            "We recommend that you or another adult is present to show what needs to be picked up. It is also possible to leave instructions if the items are easily accessible, for example in an open storage unit or outside the entrance.",
        },
        {
          question: "Which municipalities around Stockholm do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Stockholm in 3 easy steps",
        step2Text:
          "Our young workers in Stockholm come to you and collect whatever you want removed. We handle heavy lifting, tight staircases and narrow elevators – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Stockholm today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the Stockholm region.",
      },
    },
  },

  {
    slug: "transporthjalp-stockholm",
    serviceCategory: "transport",
    city: "stockholm",

    meta: {
      title: "Transporthjälp Stockholm – Skåpbil & Personal | Ungdomskraft",
      description:
        "Boka transporthjälp i Stockholm. Vi hämtar, bär och levererar möbler, vitvaror och allt annat med skåpbil – Blocket-köp, butikshämtning eller flytt. Fast pris, ofta samma dag.",
      h1: "Transporthjälp Stockholm",
    },

    hero: {
      heading: "Transporthjälp i Stockholm",
      intro:
        "Boka transporthjälp i Stockholm – vi hämtar, bär, lastar och levererar möbler, vitvaror, elektronik och allt annat med skåpbil. Från Blocket-köp och butikshämtning till flytt och förråd. Fast pris, snabb bokning och ofta tillgängliga redan samma dag i hela Stockholmsregionen.",
      heroImage: "/hero-transporthjalp.png",
    },

    featureCards: {
      headingText: "Vi hjälper dig med transporten i Stockholm",
      sinceText: "Transporthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder med alla typer av transporter i Stockholmsområdet. Möbler, vitvaror, elektronik, kartonger, Blocket-köp, butikshämtningar och flytt – vi tar hand om allt.",
      bookYourText: "Boka din transporthjälp",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Rätt skåpbil för varje transport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en enda sak eller fylla hela lastutrymmet. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Saker vi hjälper dig att transportera i Stockholm",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober, hyllor och allt annat – köpt på Blocket, från butik eller vid flytt",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys, diskmaskin och spis – vi hanterar tunga vitvaror säkert",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, skärmar, spelkonsoler och ljudanläggningar – varsam hantering",
          image: "/tv.webp",
        },
        {
          name: "Kartonger & lådor",
          description:
            "Flyttkartonger, paket, lådor och säckar – till och från förråd, vid flytt eller leverans",
          image: "/box.webp",
        },
        {
          name: "Trädgård & utemöbler",
          description:
            "Trädgårdsmöbler, grill, krukor, cyklar och säsongsprylar",
          image: "/patio-furniture.webp",
        },
        {
          name: "Övrigt",
          description:
            "Konst, byggmaterial, kontorsutrustning, träningsutrustning – finns det, kör vi det",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Transporthjälp i Stockholm – skåpbil och personal som kör allt från A till B",
      mainText:
        "Ibland behöver du bara hjälp att flytta något från en plats till en annan i Stockholm. En möbel du köpt på Blocket i Nacka, en tvättmaskin som ska till nya lägenheten på Södermalm, kartonger till förrådet i Solna eller en TV från Elgiganten i Kungens Kurva. Det är precis det vi gör.\n\nUngdomskrafts transporthjälp i Stockholm ger dig en skåpbil och ett team av starka, motiverade ungdomar som hämtar, bär, lastar, kör och levererar. Vi hämtar på vilken adress som helst i Stockholmsregionen – hemma hos en Blocket-säljare på Kungsholmen, en butik i Barkarby, ett utlämningsställe i Täby, ett förråd i Huddinge eller din gamla bostad i Bromma. Vi bär ut, skyddar med möbelfiltar, lastar säkert i skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats.\n\nStockholm innebär ofta logistiska utmaningar: trånga trapphus i sekelskifteshus på Vasastan, smala hissar i funkisbyggnader på Gärdet, begränsade parkeringsmöjligheter i innerstaden och trafikintensiva pendlarvägar under rusningstid. Våra medarbetare är vana vid alla dessa förutsättningar och planerar alltid rutten i förväg för att minimera tid och kostnad.",
      preparationHeading: "Förbered inför din transport i Stockholm",
      preparationText:
        "Beskriv så detaljerat du kan vad som ska transporteras – typ av föremål, ungefärlig storlek och vikt. Ju mer vi vet desto bättre kan vi planera fordon och team.\n\nDemontera möbler som inte går genom dörrar hela. Stäng av vatten och el till vitvaror. Koppla loss kablar från elektronik. Ha allt redo vid dörren eller i det rum det ska hämtas. Se till att det finns fri passage genom trapphus och att skåpbilen kan parkera nära porten.\n\nI centrala Stockholm kan parkeringssituationen vara utmanande – om du har tillgång till innergård, garageinfart eller lastzon, meddela oss det vid bokningen. Ha portkod och nycklar redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Transporthjälp för Blocket-köp i Stockholm",
          content:
            "Hittat en soffa på Blocket i Hägersten, ett matbord på Facebook Marketplace i Solna eller ett kylskåp på Tradera i Järfälla – men har ingen bil? Vi hämtar ditt köp hos säljaren, bär ut, lastar och kör hem till dig. Du slipper hyra bil, koordinera med kompisar eller betala för dyr hemleverans. Ange säljarens adress som hämtpunkt vid bokning – vi sköter resten.\n\nVi hanterar alla typer av Blocket-köp i Stockholmsområdet: soffor, vitvaror, elektronik, möbler, cyklar och allt annat. Vår transporthjälp är det snabbaste och enklaste sättet att hämta ditt köp – ofta redan samma dag som affären.",
        },
        {
          heading: "Transporthjälp från butik i Stockholm",
          content:
            "Köpt möbler på IKEA Kungens Kurva, en tvättmaskin på Elgiganten i Barkarby eller trädgårdsmöbler på Bauhaus i Solna – men leveransen passar inte eller kostar för mycket? Vi hämtar i butiken och kör hem till dig. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax, Biltema – oavsett butik i Stockholmsområdet hämtar vi och levererar direkt till din adress.\n\nVi är vana vid alla större handelsområden i Stockholm: Kungens Kurva, Barkarby Handelsplats, Arninge, Sickla köpkvarter och Farsta Centrum. Du bokar, vi hämtar och levererar – enkelt.",
        },
        {
          heading: "Stadsdelar och kommuner vi kör i",
          content:
            "Vi utför transporthjälp i hela Stockholmsregionen. I innerstaden (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen) navigerar vi trånga trapphus och begränsade parkeringsmöjligheter med rutin. I söderort (Farsta, Skarpnäck, Enskede, Bandhagen, Älvsjö) och västerort (Bromma, Vällingby, Hässelby, Spånga) når vi snabbt fram tack vare ringvägar och motorleder.\n\nSamma gäller för kranskommuner som Nacka, Huddinge, Sollentuna, Järfälla, Täby, Lidingö, Solna och Sundbyberg – vi tar oss dit utan extra avgifter. Behöver du transporthjälp mellan två kommuner, till exempel från Nacka till Täby? Inga problem – vi kör tvärs genom regionen.\n\nTransporthjälp ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden.",
        },
        {
          heading: "Varför boka transporthjälp i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Stockholms stadsdelar, trafikmönster och handelsområden utan och innan. Vi vet var man parkerar på Södermalm, vilka portkoder som behövs i Vasastan och snabbaste vägen från Kungens Kurva till Nacka.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för innerstaden och inga offertförfrågningar.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Dina ägodelar, trapphuset och fastigheten är skyddade under hela transporten.",
            },
            {
              label: "Allt ingår –",
              text: "Skåpbil, personal, bärning i trappor, möbelfiltar, bärremmar och spännband – allt ingår. Du behöver inte lyfta ett finger.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transporthjälp i Stockholm?",
        answer:
          "Priset beror på vad som ska transporteras, avstånd och om bärning i trappor krävs. En enstaka möbel eller vitvara kostar ofta 595–995 kr. Flera tunga saker kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på transporthjälp i Stockholm?",
        answer:
          "Transporthjälp ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning.",
      },
      {
        question: "Hur snabbt kan ni hjälpa med transport i Stockholm?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Ingår skåpbil i transporthjälpen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni hämta saker jag köpt på Blocket i Stockholm?",
        answer:
          "Ja, vi hämtar ditt Blocket-köp hos säljaren var som helst i Stockholmsregionen, bär ut, lastar och levererar hem till dig. Ange säljarens adress vid bokning.",
      },
      {
        question: "Kan ni hämta från butik i Stockholm?",
        answer:
          "Ja, vi hämtar från IKEA Kungens Kurva, Elgiganten Barkarby, Mio, JYSK, Power, Bauhaus, Byggmax och alla andra butiker i Stockholmsområdet. Ange butikens adress vid bokning.",
      },
      {
        question: "Bär ni sakerna i trappor?",
        answer:
          "Ja, vi bär i alla typer av trapphus – smala sekelskiftestrappor på Södermalm, svängda trappor på Östermalm och trappor utan hiss. Vi har säckkärra, bärremmar och rätt teknik.",
      },
      {
        question: "Skyddar ni sakerna under transport?",
        answer:
          "Ja, vi skyddar med möbelfiltar, bärremmar och spännband. TV-apparater transporteras stående. Vitvaror säkras med remmar. Allt är försäkrat genom Trygg Hansa.",
      },
      {
        question: "Kan ni hjälpa med transport vid flytt i Stockholm?",
        answer:
          "Ja, vår transporthjälp är perfekt när du packat själv men behöver hjälp att köra sakerna till nya bostaden. Vi hämtar, lastar och levererar till din nya adress i Stockholmsregionen.",
      },
      {
        question: "Kan ni köra saker till förråd i Stockholm?",
        answer:
          "Ja, vi kör möbler, kartonger och annat till och från förråd. Vi kör till alla förrådsanläggningar i Stockholmsområdet – Pelican, Shurgard, Green Storage och fler.",
      },
      {
        question: "Kan ni göra flera stopp i samma transport?",
        answer:
          "Ja, du kan lägga till flera hämtadresser och leveransadresser i samma bokning. Praktiskt om du ska hämta från Blocket-säljare i Solna och sedan köra till din lägenhet på Kungsholmen.",
      },
      {
        question: "Kan jag boka transporthjälp i Stockholm på helgen?",
        answer:
          "Ja, vi erbjuder transporthjälp alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst.",
      },
      {
        question: "Demonterar ni möbler innan transport?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär. Vi monterar även ihop dem på leveransadressen om du vill.",
      },
      {
        question: "Vilka stadsdelar i Stockholm täcker ni?",
        answer:
          "Vi kör transporthjälp i hela Stockholm – innerstaden (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), söderort (Farsta, Enskede, Bandhagen, Älvsjö), västerort (Bromma, Vällingby, Hässelby, Spånga) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Stockholm erbjuder ni transporthjälp i?",
        answer: `Vi erbjuder transporthjälp i hela Stockholmsregionen: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transporthjälp i Stockholm i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Stockholm hämtar sakerna, bär ut, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats.",
      step3Title: "Levererat",
      step3Desc:
        "Dina saker står där du vill ha dem – säkert transporterade och snyggt inställda. Klart.",
      ctaHeading: "Boka transporthjälp i Stockholm idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-help-stockholm",
      meta: {
        title: "Transport Help Stockholm – Van & Crew | Ungdomskraft",
        description:
          "Book transport help in Stockholm. We pick up, carry and deliver furniture, appliances and more with a van. Fixed price, often same day. Book today!",
        h1: "Transport Help Stockholm",
      },
      hero: {
        heading: "Transport Help in Stockholm",
        intro:
          "Book transport help in Stockholm – we pick up, carry, load and deliver furniture, appliances, electronics and anything else with a van. From Blocket purchases and store pickups to moving and storage. Fixed price, quick booking and often available the same day across the Stockholm region.",
      },
      featureCards: {
        headingText: "We help you with transport in Stockholm",
        sinceText: "Transport help since 2014",
        bookYourText: "Book your transport help",
        bookDescText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "The right van for every transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're transporting a single item or filling the entire cargo area. Furniture blankets, straps and tie-downs always included.",
      },
      itemsSection: {
        heading: "Things we help you transport in Stockholm",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes, shelves and everything else — bought on Blocket, from a store or when moving",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers, dishwashers and cookers — we handle heavy appliances safely",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, monitors, game consoles and audio systems — careful handling",
            image: "/tv.webp",
          },
          {
            name: "Boxes & packages",
            description:
              "Moving boxes, parcels, crates and bags — to and from storage, when moving or for delivery",
            image: "/box.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, barbecues, pots, bicycles and seasonal items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Everything else",
            description:
              "Artwork, building materials, office equipment, gym equipment — if it exists, we transport it",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Transport help in Stockholm – van and crew that move anything from A to B",
        mainText:
          "Sometimes you just need help moving something from one place to another in Stockholm. A piece of furniture you bought on Blocket in Nacka, a washing machine going to your new flat in Södermalm, boxes heading to storage in Solna or a TV from Elgiganten at Kungens Kurva. That's exactly what we do.\n\nUngdomskraft's transport help in Stockholm gives you a van and a team of strong, motivated young workers who pick up, carry, load, drive and deliver. We collect from any address across the Stockholm region — a Blocket seller's home in Kungsholmen, a store in Barkarby, a collection point in Täby, a storage facility in Huddinge or your old home in Bromma. We carry out, protect with furniture blankets, load safely in the van and drive to the delivery address. There we carry in and place everything where you want it.\n\nStockholm often involves logistical challenges: narrow stairways in turn-of-the-century buildings in Vasastan, small elevators in functionalist buildings on Gärdet, limited parking in the inner city and busy commuter routes during rush hour. Our workers are accustomed to all these conditions and always plan the route in advance to minimise time and cost.",
        preparationHeading: "Prepare for your transport in Stockholm",
        preparationText:
          "Describe as thoroughly as you can what needs transporting — type of item, approximate size and weight. The more we know the better we can plan the right vehicle and team.\n\nDisassemble furniture that won't fit through doors whole. Turn off water and electricity to appliances. Disconnect cables from electronics. Have everything ready by the door or in the room where it needs collecting. Make sure there is clear passage through stairwells and the van can park near the entrance.\n\nIn central Stockholm, parking can be challenging — if you have access to a courtyard, garage entrance or loading zone, let us know when booking. Have entry codes and keys ready for both addresses.",
        additionalSections: [
          {
            heading: "Transport help for Blocket purchases in Stockholm",
            content:
              "Found a sofa on Blocket in Hägersten, a dining table on Facebook Marketplace in Solna or a fridge on Tradera in Järfälla — but don't have a car? We collect your purchase from the seller, carry out, load and deliver to your home. No need to rent a car, coordinate with friends or pay for expensive delivery. Enter the seller's address as the pickup point when booking — we take care of the rest.\n\nWe handle all types of Blocket purchases across the Stockholm area: sofas, appliances, electronics, furniture, bicycles and anything else. Our transport help is the fastest and easiest way to collect your purchase — often the same day as the deal.",
          },
          {
            heading: "Transport help from stores in Stockholm",
            content:
              "Bought furniture at IKEA Kungens Kurva, a washing machine at Elgiganten in Barkarby or garden furniture at Bauhaus in Solna — but the delivery doesn't suit you or costs too much? We collect from the store and deliver to your home. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax, Biltema — whatever the store in the Stockholm area, we pick up and deliver straight to your door.\n\nWe know all the major retail parks in Stockholm: Kungens Kurva, Barkarby Handelsplats, Arninge, Sickla and Farsta Centrum. You book, we collect and deliver — simple.",
          },
          {
            heading: "Neighborhoods and municipalities we serve",
            content:
              "We provide transport help across the entire Stockholm region. In the inner city (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen) we navigate narrow stairways and limited parking with routine. In the southern suburbs (Farsta, Skarpnäck, Enskede, Bandhagen, Älvsjö) and western suburbs (Bromma, Vällingby, Hässelby, Spånga) we reach you quickly via ring roads and motorways.\n\nThe same goes for surrounding municipalities like Nacka, Huddinge, Sollentuna, Järfälla, Täby, Lidingö, Solna and Sundbyberg — no extra charges. Need transport between two municipalities, for example from Nacka to Täby? No problem — we drive across the entire region.\n\nTransport help does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost.",
          },
          {
            heading: "Why book transport help in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Stockholm's neighborhoods, traffic patterns and retail parks inside and out. We know where to park in Södermalm, which entry codes are needed in Vasastan and the fastest route from Kungens Kurva to Nacka.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region. We can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for the inner city and no quote requests.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your belongings, stairwell and property are protected throughout the transport.",
              },
              {
                label: "Everything included –",
                text: "Van, crew, stair carrying, furniture blankets, straps and tie-downs — everything is included. You don't have to lift a finger.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does transport help in Stockholm cost?",
          answer:
            "The price depends on what needs transporting, distance and whether stair carrying is required. A single piece of furniture or appliance often costs SEK 595–995. Multiple heavy items can cost SEK 1,495–2,495. You see the price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on transport help in Stockholm?",
          answer:
            "Transport help does not qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking.",
        },
        {
          question: "How quickly can you help with transport in Stockholm?",
          answer:
            "We have vans and staff across the Stockholm region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you pick up things I bought on Blocket in Stockholm?",
          answer:
            "Yes, we collect your Blocket purchase from the seller anywhere in the Stockholm region, carry out, load and deliver to your home. Enter the seller's address when booking.",
        },
        {
          question: "Can you collect from stores in Stockholm?",
          answer:
            "Yes, we collect from IKEA Kungens Kurva, Elgiganten Barkarby, Mio, JYSK, Power, Bauhaus, Byggmax and all other stores in the Stockholm area. Enter the store address when booking.",
        },
        {
          question: "Do you carry items up stairs?",
          answer:
            "Yes, we carry in all types of stairwells — narrow turn-of-the-century stairs in Södermalm, winding stairs in Östermalm and buildings without lifts. We have hand trucks, straps and the right technique.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we protect with furniture blankets, carrying straps and tie-downs. TVs are transported upright. Appliances are secured with straps. Everything is insured through Trygg Hansa.",
        },
        {
          question: "Can you help with transport when moving in Stockholm?",
          answer:
            "Yes, our transport help is perfect when you've packed yourself but need help getting everything to the new home. We collect, load and deliver to your new address anywhere in the Stockholm region.",
        },
        {
          question: "Can you drive items to storage in Stockholm?",
          answer:
            "Yes, we drive furniture, boxes and other items to and from storage. We serve all storage facilities in the Stockholm area — Pelican, Shurgard, Green Storage and more.",
        },
        {
          question: "Can you make multiple stops in the same transport?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Practical if you need to collect from a Blocket seller in Solna and then deliver to your flat in Kungsholmen.",
        },
        {
          question: "Can I book transport help in Stockholm on weekends?",
          answer:
            "Yes, we offer transport help every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you.",
        },
        {
          question: "Do you disassemble furniture before transport?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. We also reassemble them at the delivery address if you want.",
        },
        {
          question: "Which neighborhoods in Stockholm do you cover?",
          answer:
            "We provide transport help across all of Stockholm — the inner city (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), southern suburbs (Farsta, Enskede, Bandhagen, Älvsjö), western suburbs (Bromma, Vällingby, Hässelby, Spånga) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Stockholm do you serve?",
          answer:
            "We provide transport help across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Transport help in Stockholm in 3 easy steps",
        step2Text:
          "Our young workers in Stockholm collect the items, carry out, protect with furniture blankets, load the van and drive to the delivery address. There we carry in and place everything where you want it.",
        step3Title: "Delivered",
        step3Desc:
          "Your items are where you want them — safely transported and neatly placed. Done.",
        ctaHeading: "Book transport help in Stockholm today",
        ctaText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    slug: "barhjalp-stockholm",
    serviceCategory: "barhjälp",
    city: "stockholm",

    meta: {
      title: "Bärhjälp Stockholm – Starka Armar för Tunga Lyft | Ungdomskraft",
      description:
        "Boka bärhjälp i Stockholm. Vi bär möbler, vitvaror, kassaskåp och annat tungt – uppför trappor, genom trånga utrymmen och utan hiss. Fast pris, ofta samma dag.",
      h1: "Bärhjälp Stockholm",
    },

    hero: {
      heading: "Bärhjälp i Stockholm",
      intro:
        "Boka bärhjälp i Stockholm – våra starka och erfarna ungdomar bär möbler, vitvaror, kassaskåp och annat tungt uppför trappor, genom trånga utrymmen och i hisslösa hus. Du slipper riskera ryggskador och repade väggar. Fast pris, snabb bokning och ofta tillgängliga redan samma dag.",
      heroImage: "/hero-barhjalp.png",
    },

    featureCards: {
      sinceText: "Bärhjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals stockholmare med tunga lyft. Våra ungdomar är tränade i lyftteknik och vet hur man skyddar både gods och fastighet i Stockholms alla typer av trapphus.",
      bookYourText: "Boka din bärhjälp",
      bookDescText:
        "Ange din adress, beskriv vad som ska bäras och boka en tid som passar dig. Du ser priset direkt.",
      vehicleTitleText: "Rätt utrustning för ditt lyft",
      vehicleDescText:
        "Vi kommer utrustade med bärremmar, möbelfiltar, trappklättrare och skydd för golv och väggar. Rätt verktyg gör jobbet snabbare, säkrare och skonsammare för ditt hem.",
    },

    itemsSection: {
      heading: "Vanliga saker vi hjälper till att bära i Stockholm",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Tunga hörnsoffor, bäddsoffor och skrymmande fåtöljer",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskiner, torktumlare, kylskåp och frysar",
          image: "/washer.webp",
        },
        {
          name: "Träningsutrustning",
          description: "Crosstrainers, roddmaskiner, löpband och hemmagym",
          image: "/treadmill.webp",
        },
        {
          name: "Byråer & skåp",
          description: "Tunga garderober, byråer och bokhyllor i massivt trä",
          image: "/wardrobe.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & matgrupper",
          description: "Matbord i massivträ, skrivbord och konferensbord",
          image: "/table.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bärhjälp i Stockholm – för det du inte kan bära själv",
      mainText:
        "Stockholm är fullt av trapphus som inte var byggda för moderna möbler. Sekelskifteshus på Södermalm och Vasastan har smala spiraltrappor. Funkisbyggnader på Gärdet och Kungsholmen har hissar som knappt rymmer en barnvagn. Och i villakvarteren i Bromma, Enskede och Älvsjö ska tunga saker ofta bäras nerför branta källartrappor eller upp till övervåningen.\n\nDet är precis därför bärhjälp i Stockholm är en av våra mest efterfrågade tjänster. Ungdomskraft skickar starka, unga och engagerade medarbetare som är utbildade i ergonomisk lyftteknik. De vet hur man vinklar en skrymmande soffa genom ett trångt trapphus på Östermalm, hur man skyddar golv och dörrkarmar med filtar och kartong i ett landshövdingehus-inspirerat hus, och hur man arbetar säkert i trånga hissschakt och på branta trappor.\n\nVi planerar varje lyft innan vi börjar: mäter dörröppningar, identifierar risker och bestämmer den bästa vägen. Resultatet är att jobbet går snabbare, ingenting skadas och du slipper den där känslan av att 'hoppas det går bra'. Allt vi bär är försäkrat genom Trygg Hansa, och vi ersätter eventuella skador – något som inte gäller om du ber en kompis om hjälp.",
      preparationHeading: "Förbered inför bärhjälpen i Stockholm",
      preparationText:
        "Se till att vägen är fri: flytta skor, mattor och småmöbler som kan vara i vägen. Mät dörrarna om du har stora möbler – vi behöver veta om vi kan bära igenom eller om demontering krävs. Om möbler ska bäras nerför trappor, se till att trappan är fri och att belysningen fungerar.\n\nI centrala Stockholm kan parkering vara utmanande. Om du har tillgång till innergård, garageinfart eller lastzon, meddela oss det vid bokningen – det sparar tid om saker ska lastas på eller av ett fordon. Ha portkod redo om du bor i ett flerfamiljshus. Ju mer förberett, desto snabbare går det – och du sparar pengar.",
      additionalSections: [
        {
          heading: "Trapphus i Stockholm – vår specialitet",
          content:
            "Det vanligaste skälet att boka bärhjälp i Stockholm är just trapporna. Stockholms fastighetsbestånd spänner från 1700-talshus i Gamla Stan till nybyggda bostadsrätter i Hagastaden, och varje epok har sina utmaningar.\n\nSekelskifteshus på Södermalm, Vasastan och Östermalm har ofta smala, svängda trapphus med dekorativa räcken som lätt repas. Funkisbyggnader från 30- och 40-talet på Gärdet och Kungsholmen har extremt trånga hissar och branta trappor. Miljonprogramshus i Farsta, Rågsved och Skärholmen har ofta breda trapphus men saknar hiss från tredje våningen och uppåt. Villor i Bromma, Enskede, Lidingö och Täby har branta källartrappor och trånga invändiga trappor.\n\nVi har bärt möbler i alla dessa typer av trapphus och vet exakt vilken utrustning och teknik som krävs för varje situation. Med bärremmar, trappklättrare och skyddsmattor löser vi det mesta – och vi skyddar alltid väggar, räcken och golv under hela processen.",
        },
        {
          heading: "Kassaskåp, vitvaror och andra extra tunga föremål i Stockholm",
          content:
            "Kassaskåp och andra extra tunga föremål kräver särskild hantering. Ett kassaskåp kan väga allt från 100 till över 500 kg och har en kompakt, svårhanterad tyngdpunkt. Tunga vitvaror som gjutjärnsspisar, stora kombinerade kyl-frysar och professionella tvättmaskiner ställer också höga krav på teknik och utrustning. Vi har erfarenhet av att flytta den här typen av föremål i Stockholms alla typer av trapphus – från spiraltrappor i sekelskifteshus på Södermalm till branta villatrappor i Djursholm.\n\nTräningsutrustning som crosstrainers, roddmaskiner och hemmagym är en annan kategori som ofta underskattas – de är både tunga och skrymmande. Vi diskuterar alltid uppdraget i förväg: skicka gärna bilder på föremålet och trapphuset så ger vi en exakt offert. Vi använder säckkärra, remmar och ibland ramp för att flytta säkert och effektivt.\n\nBärhjälp är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Bärhjälp vid leverans och installation i Stockholm",
          listItems: [
            {
              label: "Ny möbel levererad till porten",
              text: "Vi bär den till rätt rum, packar upp och monterar vid behov. Perfekt när budföretaget bara lämnar i trapphuset eller vid porten – vanligt i Stockholms innerstadskvarter.",
            },
            {
              label: "Vitvaror in och ut",
              text: "Vi kopplar loss den gamla tvättmaskinen, bär ut den och bär in den nya. Vanligt i Stockholms äldre fastigheter där tvättstugan ligger i källaren.",
            },
            {
              label: "Möbler mellan rum",
              text: "Ska soffan byta plats med bokhyllan? Vi ommöblerar snabbt och smidigt utan att golv eller väggar tar stryk.",
            },
            {
              label: "Kontorsflytt inom huset",
              text: "Omorganisering av kontoret i Kista, Solna eller innerstaden – vi bär skrivbord, skåp och hyllor mellan rum eller våningar.",
            },
          ],
          afterContent:
            "Oavsett uppdrag i Stockholm får du ett fast pris i förväg. Inga timkostnader som skenar, inga dolda avgifter.",
        },
        {
          heading: "Varför boka bärhjälp i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi har bärt möbler i alla Stockholms stadsdelar och vet exakt vilka utmaningar varje typ av trapphus medför – från spiraltrappor på Södermalm till villakällare i Bromma.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har personal i hela Stockholmsregionen och kan ofta vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Om något skadas under bärningen ersätter vi det – trygghet som inte gäller om du ber en kompis.",
            },
            {
              label: "Fast pris –",
              text: "Du ser priset direkt vid bokning. Inga timkostnader som skenar, inga dolda avgifter och inga offertförfrågningar.",
            },
            {
              label: "Rätt utrustning –",
              text: "Vi kommer med bärremmar, trappklättrare, möbelfiltar och hörnskydd. Allt som behövs för att göra jobbet säkert och skadefritt.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bärhjälp i Stockholm?",
        answer:
          "Priset beror på vad som ska bäras, antal trappor och antal bärare. En enkel soffa upp tre trappor landar ofta på 595–1 495 kr. Du ser ett uppskattat pris direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på bärhjälp i Stockholm?",
        answer:
          "Ja, bärhjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan ni komma med bärhjälp i Stockholm?",
        answer:
          "Vi har personal i hela Stockholmsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni bära upp möbler utan hiss i Stockholm?",
        answer:
          "Ja, det är en av våra vanligaste tjänster i Stockholm. Vi bär regelbundet soffor, vitvaror och annat tungt i hisslösa trapphus – ofta fyra till fem våningar. Vi har bärremmar, trappklättrare och skyddsmattor.",
      },
      {
        question: "Hur många bärare skickar ni?",
        answer:
          "Vi skickar normalt två bärare. Vid extra tunga föremål som kassaskåp eller stora vitvaror, eller om det är många saker, kan vi skicka tre eller fyra. Ange vad som ska bäras vid bokning.",
      },
      {
        question: "Kan ni bära i trånga sekelskiftestrappor?",
        answer:
          "Ja, vi har stor erfarenhet av Stockholms smala, svängda sekelskiftestrappor – särskilt på Södermalm, Vasastan och Östermalm. Vi vet hur man vinklar skrymmande möbler och skyddar dekorativa räcken och väggar.",
      },
      {
        question: "Ingår skydd av golv och väggar?",
        answer:
          "Ja, vi skyddar alltid golv, dörrkarmar och trapphus med filtar och hörnskydd som en del av tjänsten. Du behöver inte förbereda något skyddsmaterial själv.",
      },
      {
        question: "Kan ni bära ut gamla möbler och bära in nya samma dag?",
        answer:
          "Absolut. Det är vanligt att vi bär ut en gammal soffa eller tvättmaskin och bär in den nya direkt. Beskriv bägge delarna vid bokning – vi löser det på samma besök.",
      },
      {
        question: "Är bärhjälpen i Stockholm försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Om något skadas under bärningen ersätter vi det. Dokumentera gärna föremålets skick med en bild innan vi börjar.",
      },
      {
        question: "Kan ni bära kassaskåp i Stockholm?",
        answer:
          "Ja, vi har erfarenhet av att flytta kassaskåp och andra extra tunga föremål i alla typer av stockholmska trapphus – spiraltrappor, svängda trappor och raka trappor. Vi använder säckkärra, remmar och rätt teknik. Skicka gärna bilder så ger vi en exakt offert.",
      },
      {
        question: "Kan jag boka bärhjälp i Stockholm på helgen?",
        answer:
          "Ja, vi erbjuder bärhjälp alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst.",
      },
      {
        question: "Kan ni hjälpa företag med bärhjälp i Stockholm?",
        answer:
          "Ja, vi utför bärhjälp åt både privatpersoner och företag i Stockholm. Vi bär kontorsmöbler, serverrack, kopiatorer och tung kontorsutrustning mellan rum, våningar eller byggnader. Faktura med moms – inga problem.",
      },
      {
        question: "Kan ni bära saker mellan rum inom bostaden?",
        answer:
          "Ja, vi erbjuder bärhjälp även inom bostaden. Flytta en tung soffa till ett annat rum, bära en byrå från övervåningen till källaren eller ommöblera hela vardagsrummet. Vi skyddar golv och dörrar under hela processen.",
      },
      {
        question: "Vilka stadsdelar i Stockholm täcker ni?",
        answer:
          "Vi utför bärhjälp i hela Stockholm – innerstaden (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), söderort (Farsta, Enskede, Bandhagen, Älvsjö), västerort (Bromma, Vällingby, Hässelby, Spånga) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Stockholm erbjuder ni bärhjälp i?",
        answer: `Vi erbjuder bärhjälp i hela Stockholmsregionen: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.barhjalp,

    pricingRows: getPackagePricingRows("barhjalp", "sv"),

    dynamicHeadings: {
      threeSteps: "Bärhjälp i Stockholm i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Stockholm skyddar golv och väggar, bär föremålet dit det ska – uppför trappor, genom trånga dörrar eller mellan rum – och ser till att allt står säkert på plats.",
      step3Title: "Uppdraget klart",
      step3Desc:
        "Dina saker står säkert på plats. Vi lämnar inte förrän du är nöjd och allt ser bra ut.",
      ctaHeading: "Boka bärhjälp i Stockholm idag",
      ctaText:
        "Ange din adress, beskriv vad som ska bäras och välj en tid som passar. Du ser priset direkt – vi tar hand om det tunga.",
    },

    en: {
      slug: "carrying-help-stockholm",
      meta: {
        title: "Carrying Help Stockholm – Strong Arms for Heavy Lifts | Ungdomskraft",
        description:
          "Book heavy lifting help in Stockholm. We carry furniture, appliances, safes and more – up stairs, tight spaces, no lift needed. Fixed price, often same day.",
        h1: "Carrying Help Stockholm",
      },
      hero: {
        heading: "Carrying Help in Stockholm",
        intro:
          "Book carrying help in Stockholm — our strong and experienced young workers carry furniture, appliances, safes and other heavy items up stairs, through tight spaces and in buildings without lifts. Avoid the risk of back injuries and scratched walls. Fixed price, quick booking and often available the same day.",
      },
      featureCards: {
        sinceText: "Carrying help since 2014",
        bookYourText: "Book your carrying help",
        bookDescText:
          "Enter your address, describe what needs carrying and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "Right equipment for your lift",
        vehicleDescText:
          "We arrive equipped with carrying straps, furniture blankets, stair climbers and protection for floors and walls. The right tools make the job faster, safer and gentler on your home.",
      },
      itemsSection: {
        heading: "Common items we help carry in Stockholm",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Heavy corner sofas, sofa beds and bulky armchairs",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and freezers",
            image: "/washer.webp",
          },
          {
            name: "Gym equipment",
            description: "Cross trainers, rowing machines, treadmills and home gyms",
            image: "/armchair.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "Heavy wardrobes, dressers and solid-wood bookcases",
            image: "/wardrobe.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables",
            description: "Solid-wood dining tables, desks and conference tables",
            image: "/table.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Carrying help in Stockholm — for what you can't carry yourself",
        mainText:
          "Stockholm is full of stairwells that were never designed for modern furniture. Turn-of-the-century buildings in Södermalm and Vasastan have narrow spiral staircases. Functionalist buildings on Gärdet and Kungsholmen have lifts that barely fit a pushchair. And in the residential areas of Bromma, Enskede and Älvsjö, heavy items often need carrying down steep basement stairs or up to the upper floor.\n\nThat's exactly why carrying help in Stockholm is one of our most requested services. Ungdomskraft sends strong, young and committed workers trained in ergonomic lifting technique. They know how to angle a bulky sofa through a narrow stairwell in Östermalm, how to protect floors and door frames with blankets and cardboard, and how to work safely in tight lift shafts and on steep stairs.\n\nWe plan every lift before we start: measure doorways, identify risks and determine the best route. The result is a faster job, nothing gets damaged and you avoid that nagging feeling of 'hope this goes well'. Everything we carry is insured through Trygg Hansa, and we compensate for any damage — something that doesn't apply when you ask a friend for help.",
        preparationHeading: "Prepare for your carrying help in Stockholm",
        preparationText:
          "Make sure the route is clear: move shoes, rugs and small furniture that could be in the way. Measure your doorways if you have large furniture — we need to know whether we can carry it through or if disassembly is required. If furniture is being carried down stairs, make sure the stairway is clear and the lighting works.\n\nIn central Stockholm, parking can be challenging. If you have access to a courtyard, garage entrance or loading zone, let us know when booking — it saves time if items need loading onto a vehicle. Have entry codes ready if you live in an apartment building. The more prepared you are, the faster it goes — and you save money.",
        additionalSections: [
          {
            heading: "Stairwells in Stockholm — our speciality",
            content:
              "The most common reason to book carrying help in Stockholm is the stairs. Stockholm's building stock spans from 18th-century houses in Gamla Stan to newly built condos in Hagastaden, and every era has its challenges.\n\nTurn-of-the-century buildings in Södermalm, Vasastan and Östermalm often have narrow, winding stairwells with decorative railings that scratch easily. Functionalist buildings from the 1930s and 40s on Gärdet and Kungsholmen have extremely tight lifts and steep stairs. Million Programme buildings in Farsta, Rågsved and Skärholmen often have wide stairwells but lack lifts above the third floor. Villas in Bromma, Enskede, Lidingö and Täby have steep basement stairs and tight internal staircases.\n\nWe have carried furniture in all these types of stairwells and know exactly what equipment and technique is required for each situation. With carrying straps, stair climbers and protective mats we handle most situations — and we always protect walls, railings and floors throughout the process.",
          },
          {
            heading: "Safes, appliances and other extra-heavy items in Stockholm",
            content:
              "Safes and other extra-heavy items require special handling. A safe can weigh anything from 100 to over 500 kg and has a compact, awkward centre of gravity. Heavy appliances such as cast-iron stoves, large combined fridge-freezers and commercial washing machines also demand the right technique and equipment. We have experience moving these kinds of items in all types of Stockholm stairwells — from spiral staircases in Södermalm to steep villa stairs in Djursholm.\n\nGym equipment like cross trainers, rowing machines and home gyms is another category that is often underestimated — heavy and bulky at the same time. We always discuss the job in advance: send us photos of the item and the stairwell and we'll give you an exact quote. We use sack barrows, straps and sometimes ramps to move safely and efficiently.\n\nCarrying help is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Carrying help for deliveries and installation in Stockholm",
            listItems: [
              {
                label: "New furniture delivered to the entrance",
                text: "We carry it to the right room, unpack and assemble if needed. Perfect when the courier just leaves it in the stairwell or at the entrance — common in Stockholm's inner-city blocks.",
              },
              {
                label: "Appliances in and out",
                text: "We disconnect the old washing machine, carry it out and bring the new one in. Common in Stockholm's older buildings where the laundry room is in the basement.",
              },
              {
                label: "Furniture between rooms",
                text: "Want to swap the sofa and the bookcase? We rearrange quickly and smoothly without floors or walls taking a beating.",
              },
              {
                label: "In-office moves",
                text: "Office reorganisation in Kista, Solna or the inner city — we carry desks, cabinets and shelving between rooms or floors.",
              },
            ],
            afterContent:
              "Whatever the job in Stockholm, you get a fixed price upfront. No runaway hourly rates, no hidden fees.",
          },
          {
            heading: "Why book carrying help in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We have carried furniture across all of Stockholm's neighborhoods and know exactly what challenges each type of stairwell presents — from spiral stairs in Södermalm to villa basements in Bromma.",
              },
              {
                label: "Fast availability –",
                text: "We have staff across the Stockholm region and can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. If anything is damaged during the carrying, we compensate — peace of mind that doesn't apply when you ask a friend.",
              },
              {
                label: "Fixed price –",
                text: "You see the price immediately when booking. No runaway hourly rates, no hidden fees and no quote requests.",
              },
              {
                label: "Right equipment –",
                text: "We arrive with carrying straps, stair climbers, furniture blankets and corner guards. Everything needed to do the job safely and damage-free.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does carrying help in Stockholm cost?",
          answer:
            "The price depends on what needs carrying, the number of floors and the number of carriers. A single sofa up three flights often costs SEK 595–1,495. You see an estimated price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on carrying help in Stockholm?",
          answer:
            "Yes, carrying help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "How quickly can you come with carrying help in Stockholm?",
          answer:
            "We have staff across the Stockholm region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Can you carry furniture up stairs with no lift in Stockholm?",
          answer:
            "Yes, it's one of our most common services in Stockholm. We regularly carry sofas, appliances and other heavy items in buildings without lifts — often four to five floors. We have carrying straps, stair climbers and protective mats.",
        },
        {
          question: "How many carriers do you send?",
          answer:
            "We normally send two carriers. For extra-heavy items like safes or large appliances, or when there are many items, we can send three or four. Specify what needs carrying when booking.",
        },
        {
          question: "Can you carry in narrow turn-of-the-century stairwells?",
          answer:
            "Yes, we have extensive experience with Stockholm's narrow, winding turn-of-the-century stairs — especially in Södermalm, Vasastan and Östermalm. We know how to angle bulky furniture and protect decorative railings and walls.",
        },
        {
          question: "Is floor and wall protection included?",
          answer:
            "Yes, we always protect floors, door frames and stairwells with blankets and corner guards as part of the service. You don't need to prepare any protective material yourself.",
        },
        {
          question: "Can you carry out old furniture and bring in new the same day?",
          answer:
            "Absolutely. It's common for us to carry out an old sofa or washing machine and bring in the new one straight away. Describe both parts when booking — we handle it in the same visit.",
        },
        {
          question: "Is carrying help in Stockholm insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. If anything is damaged during the carrying, we compensate. It helps to document the item's condition with a photo before we start.",
        },
        {
          question: "Can you carry safes in Stockholm?",
          answer:
            "Yes, we have experience moving safes and other extra-heavy items in all types of Stockholm stairwells — spiral stairs, winding stairs and straight stairs. We use sack barrows, straps and the right technique. Send us photos and we'll give you an exact quote.",
        },
        {
          question: "Can I book carrying help in Stockholm on weekends?",
          answer:
            "Yes, we offer carrying help every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you.",
        },
        {
          question: "Do you provide carrying help for businesses in Stockholm?",
          answer:
            "Yes, we carry items for both private customers and businesses in Stockholm. We handle office furniture, server racks, copiers and heavy office equipment between rooms, floors or buildings. Invoicing with VAT — no problem.",
        },
        {
          question: "Can you carry items between rooms within the home?",
          answer:
            "Yes, we offer carrying help within the home as well. Move a heavy sofa to another room, carry a dresser from the upper floor to the basement or rearrange an entire living room. We protect floors and doors throughout.",
        },
        {
          question: "Which neighborhoods in Stockholm do you cover?",
          answer:
            "We provide carrying help across all of Stockholm — the inner city (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), southern suburbs (Farsta, Enskede, Bandhagen, Älvsjö), western suburbs (Bromma, Vällingby, Hässelby, Spånga) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Stockholm do you serve?",
          answer:
            "We provide carrying help across the entire Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.barhjalp,
      pricingRows: getPackagePricingRows("barhjalp", "en"),
      dynamicHeadings: {
        threeSteps: "Carrying help in Stockholm in 3 easy steps",
        step2Text:
          "Our workers in Stockholm protect floors and walls, carry the item where it needs to go — up stairs, through tight doorways or between rooms — and make sure everything is safely in place.",
        step3Title: "Job done",
        step3Desc:
          "Your items are safely in place. We don't leave until you're happy and everything looks right.",
        ctaHeading: "Book carrying help in Stockholm today",
        ctaText:
          "Enter your address, describe what needs carrying and pick a time. You see the price instantly — we handle the heavy stuff.",
      },
    },
  },

  {
    slug: "flytthjalp-stockholm",
    serviceCategory: "flytt",
    city: "stockholm",

    meta: {
      title: "Flytthjälp Stockholm – Smidig Flytt med Skåpbil | Ungdomskraft",
      description:
        "Boka flytthjälp i Stockholm. Vi packar, bär, kör och lossar – komplett flytt från dörr till dörr i hela Stockholmsregionen. Fast pris, ofta samma dag.",
      h1: "Flytthjälp Stockholm",
    },

    hero: {
      heading: "Flytthjälp i Stockholm",
      intro:
        "Boka flytthjälp i Stockholm – vi fixar hela flytten med skåpbil och ett team av starka, engagerade ungdomar. Från studentflytt på Södermalm till villaflytt i Bromma. Vi packar, bär, kör och ställer allt på plats i ditt nya hem. Fast pris, snabb bokning och ofta tillgängliga redan samma dag.",
      heroImage: "/hero-flytthjalp.png",
    },

    featureCards: {
      sinceText: "Flytthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals stockholmare att flytta smidigt och prisvärt. Våra ungdomar är tränade i packning, lyftteknik och säker lastning – och de känner Stockholms stadsdelar och trapphus utan och innan.",
      bookYourText: "Boka din flytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas och välj en tid. Du ser priset direkt – allt ingår.",
      vehicleTitleText: "Skåpbil och personal i ett paket",
      vehicleDescText:
        "Du behöver inte hyra bil eller organisera transport – vi kommer med skåpbil och team. Vi lastar, kör och lossar. Stort, litet, tungt eller skrymmande – vi har rätt fordon och tillräckligt med armar.",
    },

    itemsSection: {
      heading: "Vanliga saker vi flyttar i Stockholm",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Hörnsoffor, bäddsoffor och tunga fåtöljer – vi skyddar och bär",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, skrivbord, stolar och matgrupper",
          image: "/table.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskin, torktumlare, kylskåp och diskmaskin",
          image: "/washer.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, byråer, bokhyllor och hyllsystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Fulla kartonger med böcker, kläder, kök och prylar",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Komplett flytthjälp i Stockholm – skåpbil, personal och allt däremellan",
      mainText:
        "Att flytta i Stockholm innebär ofta speciella utmaningar. Sekelskifteshus på Södermalm och Vasastan har smala, svängda trapphus där en hörnsoffa knappt tar sig runt hörnet. Funkisbyggnader på Gärdet och Kungsholmen har minihissar som bara rymmer kartonger. Miljonprogramshus i Farsta, Skärholmen och Rågsved saknar ofta hiss från tredje våningen. Och villaområden i Bromma, Enskede och Täby har källare, garage och ibland flera våningar som ska tömmas.\n\nMed Ungdomskrafts flytthjälp i Stockholm får du ett team av starka ungdomar och en skåpbil som kör hela sträckan – från gamla bostaden till den nya. Vi packar det som inte redan är packat, demonterar möbler som behöver tas isär, bär ut allt genom Stockholms alla typer av trapphus, lastar skåpbilen smart och säkert och kör till nya adressen. Sedan bär vi in allt, ställer möblerna där du vill ha dem och monterar ihop det som behöver skruvas.\n\nVi planerar varje flytt utifrån Stockholms förutsättningar: parkeringstillstånd i innerstaden, bästa rutten förbi Essingeleden och Södra länken, och vilken tid på dagen som minimerar tid i trafiken. Våra team känner stadsdelarna och vet vilka trapphus som kräver extra skydd, var hissproblem är vanliga och hur man bäst navigerar trånga innergårdar i Gamla Stan och Östermalm.",
      preparationHeading: "Förbered inför din flytt i Stockholm",
      preparationText:
        "Packa så mycket som möjligt i kartonger innan vi kommer – det sparar tid och pengar. Märk kartongerna med rum (kök, sovrum, badrum) så vi kan ställa allt på rätt plats direkt. Demontera möbler som inte går genom dörrar hela: IKEA-garderober, sängramar och stora matbord. Töm kylskåp och frys minst ett dygn innan.\n\nI centrala Stockholm kan parkering vara utmanande – om du har tillgång till innergård, garageinfart eller lastzon, meddela oss vid bokningen. Vi kan även ansöka om tillfälligt parkeringstillstånd i innerstaden om det behövs. Ha portkod och nycklar redo för båda adresserna och informera grannar om att det kan bli lite spring i trapphuset.",
      additionalSections: [
        {
          heading: "Studentflytt i Stockholm",
          content:
            "Stockholm har tiotusentals studenter som byter boende varje termin – från korridor i Lappis till etta på Söder, från studentrum i Kista till delad tvåa i Solna. Vår studentflytt-lösning i Stockholm är anpassad för mindre bohag: ett team på två personer och en skåpbil. Perfekt vid kontraktsbyten, examensflyttar och lägenhetsbyte. Vi packar, bär och kör – du behöver inte göra annat än att öppna dörren. Fast pris som du ser redan vid bokning.",
          cta: { text: "Se bärhjälp flytt →", href: "/barhjalp-flytt" },
        },
        {
          heading: "Familjeflytt i Stockholm – villa, radhus och stor lägenhet",
          content:
            "Vid en familjeflytt i Stockholm skalar vi upp teamet. En fyra på Kungsholmen med fullt bohag, en villa i Bromma med källare och garage, eller ett radhus i Enskede med trädgårdsmöbler – vi anpassar antal bärare och fordon efter ditt behov. Vi gör flera vändor om det krävs och planerar rutten så att flytten går effektivt. Tunga vitvaror kopplas loss och ansluts på nya adressen. Vi tar hand om allt från kassaskåp och vitvaror till barnens cyklar och trampolin.",
        },
        {
          heading: "Stadsdelar och kommuner vi flyttar i",
          content:
            "Vi utför flytthjälp i hela Stockholmsregionen. I innerstaden (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen, Gamla Stan) navigerar vi trånga trapphus, innergårdar och begränsade parkeringsmöjligheter med rutin. I söderort (Farsta, Enskede, Bandhagen, Älvsjö, Hägersten, Skärholmen) och västerort (Bromma, Vällingby, Hässelby, Spånga, Blackeberg) når vi snabbt fram via Södra länken och Essingeleden.\n\nSamma gäller för kranskommuner som Solna, Sundbyberg, Nacka, Lidingö, Danderyd, Täby, Järfälla, Huddinge, Haninge och alla övriga kommuner i länet – vi kör utan extra avgifter. Flyttar du mellan kommuner, till exempel från Solna till Nacka? Inga problem – vi kör tvärs genom regionen.\n\nFlytthjälp är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka flytthjälp i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi har flyttat tusentals stockholmare och vet exakt vilka utmaningar varje stadsdel medför – från spiraltrappor på Södermalm till villakällare i Bromma och studentkorridorer i Kista.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för innerstaden och inga offertförfrågningar.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla flytt är försäkrade genom Trygg Hansa. Dina ägodelar, trapphuset och fastigheten är skyddade under hela flytten.",
            },
            {
              label: "Allt ingår –",
              text: "Skåpbil, personal, bärning i trappor, möbelfiltar, bärremmar och spännband – allt ingår. Du behöver inte lyfta ett finger.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar flytthjälp i Stockholm?",
        answer:
          "Priset beror på mängden bohag, avstånd och antal bärare. En studentflytt från en etta landar ofta på 995–2 495 kr. En familjeflytt från en trea kan landa på 2 495–4 995 kr. Du ser priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på flytthjälp i Stockholm?",
        answer:
          "Ja, flytthjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan ni komma med flytthjälp i Stockholm?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Ingår skåpbil i flytthjälpen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt bohag – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni flytta vitvaror som tvättmaskin och kylskåp?",
        answer:
          "Ja, vi flyttar alla typer av vitvaror. Vi kopplar loss och säkrar dem för transport, och kan ansluta dem igen på nya adressen. Töm kylskåp och frys minst ett dygn innan flytten.",
      },
      {
        question: "Bär ni i trappor utan hiss i Stockholm?",
        answer:
          "Ja, vi bär regelbundet i hisslösa trapphus – ofta fyra till fem våningar. Vi har stor erfarenhet av Stockholms alla trapptyper: smala sekelskiftestrappor, funkistrappor och miljonprogramstrappor.",
      },
      {
        question: "Hur många bärare skickar ni?",
        answer:
          "Vi anpassar teamet efter din flytt. En etta eller tvåa behöver oftast två bärare. En trea eller fyra med mycket möbler kan kräva tre till fyra personer. Ange vad som ska flyttas vid bokning.",
      },
      {
        question: "Skyddar ni möblerna under flytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband. Vi skyddar golv, väggar och dörrkarmar vid ut- och inbärning. Allt är försäkrat genom Trygg Hansa.",
      },
      {
        question: "Kan ni hjälpa med flytt på helger?",
        answer:
          "Ja, vi erbjuder flytthjälp alla dagar i veckan, inklusive lördagar och söndagar. Många kunder föredrar helgflytt för att slippa ta ledigt från jobbet.",
      },
      {
        question: "Demonterar och monterar ni möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär. På nya adressen monterar vi ihop allt igen. Spara gärna skruvar och beslag i märkta påsar.",
      },
      {
        question: "Kan ni göra flera vändor under samma flytt?",
        answer:
          "Ja, vid stor flytt – till exempel villa med källare och garage – gör vi flera vändor med skåpbilen. Vi planerar antalet vändor i förväg baserat på ditt bohags storlek.",
      },
      {
        question: "Erbjuder ni kontorsflytt i Stockholm?",
        answer:
          "Ja, vi utför kontorsflytt i hela Stockholmsregionen – från enskilda arbetsplatser i Kista till hela kontor i innerstaden. Vi hanterar skrivbord, kontorsstolar, arkivskåp och IT-utrustning.",
      },
      {
        question: "Kan jag få flytthjälp samma vecka?",
        answer:
          "Ja, vi har ofta lediga tider med kort varsel. Många kunder bokar flytt redan för samma vecka. Boka online och se direkt vilka tider som är tillgängliga.",
      },
      {
        question: "Vilka stadsdelar i Stockholm täcker ni?",
        answer:
          "Vi utför flytthjälp i hela Stockholm – innerstaden (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), söderort (Farsta, Enskede, Bandhagen, Älvsjö), västerort (Bromma, Vällingby, Hässelby, Spånga) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Stockholm erbjuder ni flytthjälp i?",
        answer: `Vi erbjuder flytthjälp i hela Stockholmsregionen: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Flytthjälp i Stockholm i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Stockholm packar, bär ut allt, lastar skåpbilen, kör till nya adressen och bär in allt på rätt plats. Du behöver inte lyfta ett finger – vi sköter hela flytten.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi lossar och bär in allt i ditt nya hem. Möbler ställs på rätt plats och det som demonterats monteras ihop igen. Du kan börja leva i nya hemmet direkt.",
      ctaHeading: "Boka flytthjälp i Stockholm idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv ditt bohag och välj en tid. Du ser priset direkt – skåpbil och personal ingår.",
    },

    en: {
      slug: "moving-help-stockholm",
      meta: {
        title: "Moving Help Stockholm – Van & Crew | Ungdomskraft",
        description:
          "Book moving help in Stockholm – we pack, carry, drive and unload. A complete door-to-door move across the Stockholm region. Fixed price, often same day.",
        h1: "Moving Help Stockholm",
      },
      hero: {
        heading: "Moving Help in Stockholm",
        intro:
          "Book moving help in Stockholm — we handle the entire move with a van and a team of strong, committed young workers. From student moves in Södermalm to family moves in Bromma. We pack, carry, drive and place everything in your new home. Fixed price, quick booking and often available the same day.",
      },
      featureCards: {
        sinceText: "Moving help since 2014",
        bookYourText: "Book your move",
        bookDescText:
          "Enter the old and new address, describe what needs moving and pick a time. You see the price instantly — everything is included.",
        vehicleTitleText: "Van and crew in one package",
        vehicleDescText:
          "No need to rent a vehicle or organise transport — we arrive with a van and a full team. We load, drive and unload. Big, small, heavy or bulky — we have the right vehicle and enough arms.",
      },
      itemsSection: {
        heading: "Common items we move in Stockholm",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Corner sofas, sofa beds and heavy armchairs — protected and carried",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, desks, chairs and dining sets",
            image: "/table.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and dishwashers",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, dressers, bookcases and shelving units",
            image: "/wardrobe.webp",
          },
          {
            name: "Moving boxes",
            description: "Packed boxes with books, clothes, kitchen items and belongings",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Complete moving help in Stockholm — van, crew and everything in between",
        mainText:
          "Moving in Stockholm often comes with unique challenges. Turn-of-the-century buildings in Södermalm and Vasastan have narrow, winding stairwells where a corner sofa barely makes it round the bend. Functionalist buildings on Gärdet and Kungsholmen have tiny lifts that only fit boxes. Million Programme buildings in Farsta, Skärholmen and Rågsved often lack lifts above the third floor. And residential areas in Bromma, Enskede and Täby have basements, garages and sometimes multiple floors to clear.\n\nWith Ungdomskraft's moving help in Stockholm you get a team of strong young workers and a van that drives the whole distance — from the old home to the new one. We pack what isn't already packed, disassemble furniture that needs taking apart, carry everything out through Stockholm's many types of stairwells, load the van smartly and securely and drive to the new address. Then we carry everything in, place furniture where you want it and reassemble anything that needs putting together.\n\nWe plan every move based on Stockholm's conditions: parking permits in the inner city, the best route past Essingeleden and Södra länken, and what time of day minimises time in traffic. Our teams know the neighborhoods and understand which stairwells need extra protection, where lift problems are common and how to navigate tight courtyards in Gamla Stan and Östermalm.",
        preparationHeading: "Prepare for your move in Stockholm",
        preparationText:
          "Pack as much as possible into boxes before we arrive — it saves time and money. Label boxes by room (kitchen, bedroom, bathroom) so we can put everything in the right place straight away. Disassemble furniture that won't fit through doors whole: IKEA wardrobes, bed frames and large dining tables. Empty the fridge and freezer at least a day before.\n\nIn central Stockholm, parking can be challenging — if you have access to a courtyard, garage entrance or loading zone, let us know when booking. We can also apply for a temporary parking permit in the inner city if needed. Have entry codes and keys ready for both addresses and let neighbours know there may be some traffic in the stairwell.",
        additionalSections: [
          {
            heading: "Student moves in Stockholm",
            content:
              "Stockholm has tens of thousands of students who switch housing every semester — from a corridor room at Lappis to a studio in Södermalm, from a student room in Kista to a shared flat in Solna. Our student-move solution in Stockholm is built for smaller households: a team of two and a van. Perfect for lease changes, graduation moves and flat swaps. We pack, carry and drive — all you need to do is open the door. Fixed price that you see when booking.",
            cta: { text: "Carrying help for moving →", href: "/en/carrying-help-move" },
          },
          {
            heading: "Family moves in Stockholm — houses, terraces and large flats",
            content:
              "For a family move in Stockholm we scale the team up. A four-room flat on Kungsholmen with a full household, a house in Bromma with a basement and garage, or a terraced house in Enskede with garden furniture — we adapt the number of carriers and vehicle to your needs. We make multiple trips if necessary and plan the route so the move runs efficiently. Heavy appliances are disconnected and reconnected at the new address. We handle everything from safes and appliances to children's bikes and trampolines.",
          },
          {
            heading: "Neighborhoods and municipalities we move in",
            content:
              "We provide moving help across the entire Stockholm region. In the inner city (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen, Gamla Stan) we navigate narrow stairwells, courtyards and limited parking with routine. In the southern suburbs (Farsta, Enskede, Bandhagen, Älvsjö, Hägersten, Skärholmen) and western suburbs (Bromma, Vällingby, Hässelby, Spånga, Blackeberg) we reach you quickly via Södra länken and Essingeleden.\n\nThe same goes for surrounding municipalities like Solna, Sundbyberg, Nacka, Lidingö, Danderyd, Täby, Järfälla, Huddinge, Haninge and all other municipalities in the county — no extra charges. Moving between municipalities, for example from Solna to Nacka? No problem — we drive across the entire region.\n\nMoving help is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book moving help in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We have moved thousands of Stockholm residents and know exactly what challenges each neighborhood presents — from spiral stairs in Södermalm to villa basements in Bromma and student corridors in Kista.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region. We can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for the inner city and no quote requests.",
              },
              {
                label: "Insured and safe –",
                text: "All moves are insured through Trygg Hansa. Your belongings, stairwell and property are protected throughout the move.",
              },
              {
                label: "Everything included –",
                text: "Van, crew, stair carrying, furniture blankets, straps and tie-downs — everything is included. You don't have to lift a finger.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does moving help in Stockholm cost?",
          answer:
            "The price depends on the volume of belongings, distance and number of carriers. A student move from a studio often costs SEK 995–2,495. A family move from a two- or three-bed can cost SEK 2,495–4,995. You see the price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on moving help in Stockholm?",
          answer:
            "Yes, moving help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "How quickly can you come with moving help in Stockholm?",
          answer:
            "We have vans and staff across the Stockholm region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on your household — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you move appliances like washing machines and fridges?",
          answer:
            "Yes, we move all types of appliances. We disconnect and secure them for transport, and can reconnect them at the new address. Remember to empty the fridge and freezer at least a day before.",
        },
        {
          question: "Do you carry up stairs without a lift in Stockholm?",
          answer:
            "Yes, we regularly carry in buildings without lifts — often four to five floors. We have extensive experience with all of Stockholm's stairwell types: narrow turn-of-the-century stairs, functionalist stairs and Million Programme stairs.",
        },
        {
          question: "How many carriers do you send?",
          answer:
            "We adapt the team to your move. A studio or one-bed usually needs two carriers. A two- or three-bed with lots of furniture may need three to four. Describe what needs moving when booking.",
        },
        {
          question: "Do you protect furniture during the move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect floors, walls and door frames when carrying out and in. Everything is insured through Trygg Hansa.",
        },
        {
          question: "Can you help with moving on weekends?",
          answer:
            "Yes, we offer moving help every day of the week, including Saturdays and Sundays. Many customers prefer a weekend move so they don't have to take time off work.",
        },
        {
          question: "Do you disassemble and reassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. At the new address we reassemble everything. It helps to keep screws and fittings in labelled bags.",
        },
        {
          question: "Can you make multiple trips during the same move?",
          answer:
            "Yes, for larger moves — for example a house with basement and garage — we make multiple trips with the van. We plan the number of trips in advance based on the size of your household.",
        },
        {
          question: "Do you offer office moves in Stockholm?",
          answer:
            "Yes, we carry out office moves across the Stockholm region — from individual workstations in Kista to entire offices in the inner city. We handle desks, office chairs, filing cabinets and IT equipment.",
        },
        {
          question: "Can I get moving help the same week?",
          answer:
            "Yes, we often have availability at short notice. Many customers book a move for the same week. Book online and see straight away which times are available.",
        },
        {
          question: "Which neighborhoods in Stockholm do you cover?",
          answer:
            "We provide moving help across all of Stockholm — the inner city (Södermalm, Norrmalm, Vasastan, Östermalm, Kungsholmen), southern suburbs (Farsta, Enskede, Bandhagen, Älvsjö), western suburbs (Bromma, Vällingby, Hässelby, Spånga) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Stockholm do you serve?",
          answer: `We provide moving help across the entire Stockholm region: ${stockholmKommunerText}. No extra charges for surrounding municipalities.`,
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Moving help in Stockholm in 3 easy steps",
        step2Text:
          "Our workers in Stockholm pack, carry everything out, load the van, drive to the new address and carry everything in. You don't need to lift a finger — we handle the entire move.",
        step3Title: "Everything in place",
        step3Desc:
          "We unload and carry everything into your new home. Furniture is placed where you want it and anything disassembled is reassembled. You can start living in your new home straight away.",
        ctaHeading: "Book moving help in Stockholm today",
        ctaText:
          "Enter the old and new address, describe your household and pick a time. You see the price instantly — van and crew included.",
      },
    },
  },

  // ─── Möbelmontering Stockholm ─────────────────────────────────────
  {
    slug: "mobelmontering-stockholm",
    serviceCategory: "möbelmontering",
    city: "stockholm",

    meta: {
      title: "Möbelmontering Stockholm – Snabb & Prisvärd | Ungdomskraft",
      description:
        "Boka möbelmontering i Stockholm. Vi monterar IKEA-möbler, garderober, kök och plattpack i alla stadsdelar – Södermalm, Bromma, Kungsholmen och fler. Samma dag!",
      h1: "Möbelmontering Stockholm",
    },

    hero: {
      heading: "Möbelmontering i Stockholm",
      intro:
        "Boka möbelmontering i Stockholm – vi skickar montörer med komplett verktygslåda till alla stadsdelar och kranskommuner. Från en enkel bokhylla till ett helt IKEA-kök. Fast pris, enkel bokning och ofta tillgängliga redan samma dag.",
    },

    featureCards: {
      headingText: "Vi monterar dina möbler i Stockholm",
      sinceText: "Möbelmontering sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft monterat tusentals plattpackade möbler åt stockholmare – från ettor på Södermalm till villor i Bromma. Vårt team av snabba ungdomar bygger allt med den noggrannhet och energi som krävs för ett perfekt resultat.",
      bookYourText: "Boka din möbelmontering",
      bookDescText:
        "Beskriv vilka möbler som ska monteras, ange din adress i Stockholm och se priset direkt. Vi tar med alla verktyg.",
      vehicleTitleText: "Bil med komplett verktygslåda",
      vehicleDescText:
        "Våra montörer i Stockholm kommer med bil utrustad med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba och tumstock. Du behöver inte ha några egna verktyg hemma.",
    },

    itemsSection: {
      heading: "Möbler vi monterar i Stockholm",
      items: [
        {
          name: "Bokhyllor & hyllsystem",
          description:
            "BILLY, KALLAX, EKET och andra bokhyllor och förvaringssystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Garderober",
          description:
            "PAX, PLATSA och andra garderobssystem med skjutdörrar, lådor och inredning",
          image: "/wardrobe.webp",
        },
        {
          name: "Skrivbord & kontorsmöbler",
          description:
            "Skrivbord, höj- och sänkbara bord, kontorsstolar och hurtsar",
          image: "/office-desk.webp",
        },
        {
          name: "Sängar & sängstommar",
          description:
            "Sängstommar, loftsängar, våningssängar och huvudgavlar",
          image: "/bed.webp",
        },
        {
          name: "Matbord & stolar",
          description:
            "Matgrupper, utdragbara bord, bänkar och stolar som kräver montering",
          image: "/table.webp",
        },
        {
          name: "Köksskåp & bänkskivor",
          description:
            "METOD-kök, överskåp, underskåp, bänkskivor och köksöar",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Möbelmontering i Stockholm – från plattpack till färdig möbel",
      mainText:
        "Stockholm är fullt av IKEA-kartonger som väntar på att bli möbler. Oavsett om du precis har handlat på IKEA Kungens Kurva, IKEA Barkarby eller fått en hemleverans från JYSK eller Mio, skickar vi montörer med komplett verktygslåda direkt till din dörr. Vi monterar allt från enstaka bokhyllor till kompletta garderobssystem och hela kök.\n\nStockholms bostäder ställer särskilda krav på möbelmontering. Trånga trapphus i sekelskifteshus på Vasastan gör det svårt att få upp stora kartonger – våra montörer bär upp och monterar på plats. Kompakta lägenheter på Södermalm kräver smart placering och millimeteranpassning av garderober. Nyproduktion i Hammarby Sjöstad och Hagastaden har ofta walk-in closets och skräddarsydda förvaringslösningar som behöver monteras exakt efter ritning. Vårt team har erfarenhet av alla dessa situationer och vet hur man jobbar effektivt i Stockholms varierande bostadsbestånd.\n\nVi tar med borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock och allt annat som behövs. Du behöver inte ens ha en skruvmejsel hemma.",
      preparationHeading: "Förbered inför möbelmonteringen i Stockholm",
      preparationText:
        "Se till att kartongerna med de plattpackade möblerna finns i rätt rum innan montörerna kommer. Om du bor i lägenhet i innerstaden och kartongerna är tunga, kan vi hjälpa till att bära upp dem – nämn det i bokningen. Kontrollera gärna att alla delar finns med genom att stämma av mot innehållsförteckningen. Rensa golvet så att det finns tillräckligt med arbetsyta. Om du har specifika önskemål om placering, markera gärna var möbeln ska stå. Bor du i ett sekelskifteshus med smal hiss, meddela oss så planerar vi logistiken i förväg.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi täcker i Stockholm",
          content:
            "Vi utför möbelmontering i hela Stockholmsregionen – från innerstaden till ytterstaden och kranskommunerna. I innerstaden (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen) har vi stor erfarenhet av de specifika utmaningar som äldre byggnader medför – trånga spiraltrappor, små hissar och begränsade möjligheter att parkera nära porten. Vi planerar alltid rutten och logistiken i förväg.\n\nI söderort (Farsta, Skarpnäck, Enskede, Bandhagen, Rågsved) och västerort (Vällingby, Hässelby, Spånga, Bromma) når vi er snabbt tack vare god tillgång till ringvägar och motorleder. Samma gäller för kranskommuner som Nacka, Huddinge, Sollentuna, Järfälla och Täby. Nybyggda områden som Hagastaden, Liljeholmskajen och Norra Djurgårdsstaden har ofta stora lägenheter med mycket förvaring som kräver omfattande montering – perfekt uppdrag för oss.",
        },
        {
          heading: "Kombinera hämtning och montering i Stockholm",
          content:
            "Handlat på IKEA Kungens Kurva eller IKEA Barkarby och saknar bil? Vi hämtar de plattpackade kartongerna direkt i butiken, kör hem dem till din adress i Stockholm och monterar allt på plats. Samma sak gäller för JYSK, Mio, Rusta och andra möbelbutiker i Stockholmsområdet. Allt i en enda bokning – från butikshylla till färdig möbel i ditt hem. Du slipper hyra släp, bära tunga kartonger i trappor och sedan montera allt själv.",
        },
        {
          heading: "Möbelmontering för företag och kontor i Stockholm",
          content:
            "Flyttar ditt företag in i nya lokaler i Kista, på Östermalm eller i Solna? Vi monterar kontorsmöbler i alla storlekar – från enskilda höj- och sänkbara skrivbord till kompletta mötesrumsuppsättningar och öppna kontorslandskap. Vi kan arbeta utanför kontorstid, på kvällar och helger, så att monteringen inte stör den dagliga verksamheten. Företag som anlitar oss kan inkludera våra uppdrag i sin CSR-rapportering, eftersom varje bokning sysselsätter unga stockholmare och bidrar till ungdomars väg in på arbetsmarknaden.",
        },
        {
          heading: "Varför boka möbelmontering i Stockholm med Ungdomskraft?",
          content:
            "Ungdomskraft grundades 2014 i Stockholm med en tydlig vision: att ge unga stockholmare en riktig chans på arbetsmarknaden, samtidigt som kunder får snabb och prisvärd hjälp. Tio år senare har vi genomfört över 50 000 uppdrag i Stockholmsregionen och sysselsatt hundratals ungdomar.\n\nVåra montörer är unga, läraktiga och genuint motiverade. De har gått igenom vår interna utbildning där de lär sig allt från att läsa monteringsinstruktioner effektivt till att hantera verktyg säkert och professionellt. Varje uppdrag i Stockholm är en chans att utvecklas, och det märks i kvaliteten på arbetet. De tar sig tid att göra rätt – justerar skjutdörrar tills de glider perfekt, kontrollerar att hyllplan sitter i våg och ser till att lådor öppnas och stängs utan motstånd.\n\nFör många av våra ungdomar i Stockholm är Ungdomskraft det första riktiga jobbet. Här lär de sig kundkontakt, punktlighet, problemlösning och att ta ansvar för ett färdigt resultat. Det är arbetslivserfarenhet som banker, IT-företag och andra arbetsgivare värderar högt. Genom att boka möbelmontering med oss investerar du inte bara i färdiga möbler – du investerar i en ung stockholmares framtid.\n\nVi tar kvalitet på allvar. Varje uppdrag avslutas med en genomgång där montören kontrollerar att alla skruvar sitter, att dörrar stänger ordentligt och att möbeln står stadigt. Om något inte känns rätt fixar vi det på plats.\n\nHållbarhet är en naturlig del av vårt arbete. Vi tar hand om allt emballage och sorterar kartong, plast och frigolit för återvinning. Att boka möbelmontering med Ungdomskraft i Stockholm är mer än en tjänst – det är hjälp med mening.\n\nMöbelmontering är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
      ],
    },

    faq: [
      {
        question: "Vilka verktyg tar ni med till möbelmontering i Stockholm?",
        answer:
          "Vi tar med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock, hammare och allt annat som behövs. Du behöver inte ha några egna verktyg.",
      },
      {
        question: "Monterar ni IKEA-möbler i Stockholm?",
        answer:
          "Ja, IKEA-möbler är vår vanligaste möbelmontering i Stockholm. Vi har monterat tusentals PAX-garderober, BILLY-bokhyllor, KALLAX-hyllor och METOD-kök åt stockholmare. Vi känner igen varje serie och vet exakt hur de ska byggas.",
      },
      {
        question: "Kan ni montera möbler i Stockholm samma dag?",
        answer:
          "Ja, vi erbjuder möbelmontering samma dag i Stockholm om du bokar tidigt. Våra montörer kan ofta vara på plats inom några timmar, oavsett om du bor i innerstaden eller i en kranskommun.",
      },
      {
        question: "Vad kostar möbelmontering i Stockholm?",
        answer:
          "Priset beror på antal möbler och komplexitet. En enkel möbel från 595 kr (Extra Liten). En garderob eller ett par möbler från 995 kr (Small). Flera möbler från 1 495 kr (Medium). Du ser alltid det uppskattade priset innan du bokar.",
      },
      {
        question: "Kan jag få RUT-avdrag på möbelmontering i Stockholm?",
        answer:
          "Ja, möbelmontering ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Kan ni hämta möblerna på IKEA och montera hemma i Stockholm?",
        answer:
          "Ja, vi hämtar plattpackade möbler från IKEA Kungens Kurva, IKEA Barkarby eller annan butik i Stockholm, kör hem dem till dig och monterar allt på plats – i en enda bokning.",
      },
      {
        question: "Monterar ni möbler i alla Stockholms stadsdelar?",
        answer:
          "Ja, vi monterar möbler i hela Stockholm – Södermalm, Vasastan, Kungsholmen, Bromma, Farsta, Vällingby, Kista och alla andra stadsdelar. Vi täcker även alla kranskommuner som Nacka, Solna, Huddinge, Täby och Järfälla.",
      },
      {
        question: "Är mina möbler försäkrade under monteringen i Stockholm?",
        answer:
          "Ja, alla uppdrag är försäkrade. Om något skulle gå sönder under möbelmonteringen ersätter vi det. Våra montörer arbetar noggrant och varsamt med dina möbler.",
      },
      {
        question: "Tar ni hand om förpackningsmaterialet i Stockholm?",
        answer:
          "Ja, vi plockar ihop all kartong, cellplast och frigolit efter monteringen. Vi kan ta med oss allt emballage så slipper du springa till återvinningen.",
      },
      {
        question: "Kan ni montera kök i Stockholm?",
        answer:
          "Ja, vi monterar köksskåp, bänkskivor och köksöar i hela Stockholmsområdet. Vi hanterar allt snickeri – underskåp, överskåp, luckor, lådor och beslag. El- och VVS-anslutningar beställer du separat av behörig installatör.",
      },
      {
        question: "Monterar ni kontorsmöbler i Stockholm?",
        answer:
          "Ja, vi monterar kontorsmöbler i hela Stockholm – höj- och sänkbara skrivbord, kontorsstolar, hurtsar och mötesrumsmöbler. Vi kan arbeta på kvällar och helger så att monteringen inte stör verksamheten.",
      },
      {
        question: "Hur lång tid tar möbelmontering av en PAX-garderob i Stockholm?",
        answer:
          "En standard PAX-garderob med två dörrar tar cirka 45–90 minuter. Större system med skjutdörrar, lådor och inredning kan ta 2–3 timmar. Vi meddelar uppskattad tid vid bokning.",
      },
      {
        question: "Kan ni demontera möbler i Stockholm också?",
        answer:
          "Ja, vi både monterar och demonterar möbler i Stockholm. Perfekt om du ska flytta och behöver ta isär garderober, sängar eller skrivbord innan flytten.",
      },
      {
        question: "Monterar ni möbler på kvällar och helger i Stockholm?",
        answer:
          "Ja, vi erbjuder möbelmontering i Stockholm vardagar, kvällar och helger. Du väljer den tid som passar dig bäst direkt vid bokning.",
      },
      {
        question: "I vilka kommuner i Stockholmsområdet erbjuder ni möbelmontering?",
        answer:
          `Vi erbjuder möbelmontering i hela Stockholmsområdet: ${stockholmKommunerText}. Vi täcker även alla stadsdelar inom Stockholms stad.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.montering,

    pricingRows: getPackagePricingRows("montering", "sv"),

    dynamicHeadings: {
      threeSteps: "Möbelmontering i Stockholm i 3 enkla steg",
      step2Title: "Vi monterar allt",
      step2Text:
        "Våra montörer i Stockholm kommer med bil och komplett verktygslåda. De packar upp, monterar och kontrollerar att allt sitter perfekt. Kartong och emballage plockas ihop.",
      step3Title: "Klart",
      step3Desc:
        "Dina möbler är monterade, justerade och redo att användas. Emballaget är bortplockat och du kan njuta av resultatet.",
      ctaHeading: "Boka möbelmontering i Stockholm idag",
      ctaText:
        "Beskriv vilka möbler som ska monteras, ange din adress i Stockholm och se priset direkt. Vi tar med alla verktyg – du behöver inte lyfta ett finger.",
    },

    en: {
      slug: "furniture-assembly-stockholm",
      meta: {
        title: "Furniture Assembly Stockholm – Fast & Affordable | Ungdomskraft",
        description:
          "Book furniture assembly in Stockholm – IKEA, wardrobes, kitchens and all flat-pack. Available across all neighborhoods. Same day service!",
        h1: "Furniture Assembly Stockholm",
      },

      hero: {
        heading: "Furniture assembly in Stockholm",
        intro:
          "Book furniture assembly in Stockholm – we send assemblers with a complete toolbox to all neighborhoods and surrounding municipalities. From a simple bookshelf to a full IKEA kitchen. Fixed price, easy booking and often available the same day.",
      },

      featureCards: {
        headingText: "We assemble your furniture in Stockholm",
        sinceText: "Furniture assembly since 2014",
        storyText:
          "Since 2014, Ungdomskraft has assembled thousands of flat-pack furniture pieces for Stockholm residents – from studios in Södermalm to houses in Bromma. Our team of quick young assemblers builds everything with the precision and energy required for a perfect result.",
        bookYourText: "Book your assembly",
        bookDescText:
          "Describe what furniture needs assembling, enter your Stockholm address and see the price instantly. We bring all tools.",
        vehicleTitleText: "Car with a complete toolbox",
        vehicleDescText:
          "Our assemblers in Stockholm arrive by car equipped with a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet and tape measure. You don't need any tools at home.",
      },

      itemsSection: {
        heading: "Furniture we assemble in Stockholm",
        items: [
          {
            name: "Bookshelves & storage",
            description: "BILLY, KALLAX, EKET and other bookshelves and storage systems",
            image: "/wardrobe.webp",
          },
          {
            name: "Wardrobes",
            description: "PAX, PLATSA and other wardrobe systems with sliding doors, drawers and fittings",
            image: "/wardrobe.webp",
          },
          {
            name: "Desks & office furniture",
            description: "Desks, sit-stand desks, office chairs and drawer units",
            image: "/office-desk.webp",
          },
          {
            name: "Beds & bed frames",
            description: "Bed frames, loft beds, bunk beds and headboards",
            image: "/bed.webp",
          },
          {
            name: "Dining tables & chairs",
            description: "Dining sets, extendable tables, benches and chairs requiring assembly",
            image: "/table.webp",
          },
          {
            name: "Kitchen cabinets & countertops",
            description: "METOD kitchens, wall cabinets, base cabinets, countertops and kitchen islands",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Furniture assembly in Stockholm – from flat-pack to finished furniture",
        mainText:
          "Stockholm is full of IKEA boxes waiting to become furniture. Whether you just shopped at IKEA Kungens Kurva, IKEA Barkarby or received a home delivery from JYSK or Mio, we send assemblers with a complete toolbox straight to your door. We assemble everything from single bookshelves to complete wardrobe systems and entire kitchens.\n\nStockholm homes present unique assembly challenges. Narrow staircases in turn-of-the-century buildings in Vasastan make it hard to get large boxes upstairs – our assemblers carry up and assemble on site. Compact apartments in Södermalm require smart placement and millimeter-precise wardrobe fitting. New builds in Hammarby Sjöstad and Hagastaden often feature walk-in closets and custom storage solutions that need assembly exactly to plan. Our team has experience with all these situations and knows how to work efficiently in Stockholm's varied housing stock.\n\nWe bring power drills, screwdrivers, Allen keys in all sizes, spirit levels, rubber mallets, tape measures and everything else needed. You don't even need a screwdriver at home.",
        preparationHeading: "How to prepare for assembly in Stockholm",
        preparationText:
          "Make sure the flat-pack boxes are in the right room before the assemblers arrive. If you live in an apartment in the city centre and the boxes are heavy, we can help carry them up – mention it when booking. Check that all parts are included by reviewing the contents list. Clear the floor to provide workspace. If you have specific placement preferences, mark where the furniture should go. If you live in a turn-of-the-century building with a narrow lift, let us know so we can plan logistics in advance.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover in Stockholm",
            content:
              "We perform furniture assembly across the entire Stockholm region – from the city centre to the suburbs and surrounding municipalities. In the inner city (Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen), we have extensive experience with the specific challenges that older buildings present – tight spiral staircases, small lifts and limited parking near the entrance.\n\nIn the southern suburbs (Farsta, Skarpnäck, Enskede, Bandhagen, Rågsved) and western suburbs (Vällingby, Hässelby, Spånga, Bromma) we reach you quickly via ring roads and motorways. The same applies to surrounding municipalities such as Nacka, Huddinge, Sollentuna, Järfälla and Täby. New-build areas like Hagastaden, Liljeholmskajen and Norra Djurgårdsstaden often have large apartments with extensive storage requiring comprehensive assembly – a perfect job for us.",
          },
          {
            heading: "Combine pickup and assembly in Stockholm",
            content:
              "Shopped at IKEA Kungens Kurva or IKEA Barkarby and don't have a car? We pick up the flat-pack boxes directly from the store, drive them to your Stockholm address and assemble everything on site. The same applies for JYSK, Mio, Rusta and other furniture stores in the Stockholm area. All in one booking – from store shelf to finished furniture in your home. You skip renting a trailer, carrying heavy boxes up stairs and then assembling everything yourself.",
          },
          {
            heading: "Furniture assembly for businesses and offices in Stockholm",
            content:
              "Is your company moving into new premises in Kista, Östermalm or Solna? We assemble office furniture of all sizes – from individual sit-stand desks to complete meeting room setups and open-plan office landscapes. We can work outside business hours, on evenings and weekends, so the assembly doesn't disrupt daily operations. Companies that hire us can include our jobs in their CSR reporting, since every booking employs young Stockholmers and contributes to youth employment.",
          },
          {
            heading: "Why book furniture assembly in Stockholm with Ungdomskraft?",
            content:
              "Ungdomskraft was founded in 2014 in Stockholm with a clear vision: to give young Stockholmers a real chance in the job market while offering customers fast and affordable help. Ten years later, we have completed over 50,000 jobs in the Stockholm region and employed hundreds of young people.\n\nOur assemblers are young, eager to learn and genuinely motivated. They have completed our internal training programme where they learn everything from reading assembly instructions efficiently to handling tools safely and professionally. Every job in Stockholm is a chance to grow, and it shows in the quality of the work. They take the time to get it right – adjusting sliding doors until they glide perfectly, checking that shelves are level and making sure drawers open and close without resistance.\n\nFor many of our young workers in Stockholm, Ungdomskraft is their first real job. Here they learn customer interaction, punctuality, problem-solving and taking responsibility for a finished result. This is work experience that banks, tech companies and other employers value highly. By booking furniture assembly with us, you are not just investing in finished furniture – you are investing in a young Stockholmer's future.\n\nWe take quality seriously. Every job ends with a walkthrough where the assembler checks that all screws are tight, doors close properly and the furniture stands steady. If anything doesn't feel right, we fix it on the spot.\n\nSustainability is a natural part of our work. We handle all packaging and sort cardboard, plastic and polystyrene for recycling. Booking furniture assembly with Ungdomskraft in Stockholm is more than a service – it is help with purpose.\n\nFurniture assembly is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
        ],
      },

      faq: [
        {
          question: "What tools do you bring for furniture assembly in Stockholm?",
          answer:
            "We bring a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet, tape measure, hammer and everything else needed. You don't need any tools at home.",
        },
        {
          question: "Do you assemble IKEA furniture in Stockholm?",
          answer:
            "Yes, IKEA furniture is our most common assembly job in Stockholm. We have assembled thousands of PAX wardrobes, BILLY bookshelves, KALLAX shelves and METOD kitchens for Stockholm residents. We know every range and exactly how to build them.",
        },
        {
          question: "Can you assemble furniture in Stockholm the same day?",
          answer:
            "Yes, we offer same-day furniture assembly in Stockholm if you book early. Our assemblers can often be there within a few hours, whether you live in the city centre or in a surrounding municipality.",
        },
        {
          question: "How much does furniture assembly cost in Stockholm?",
          answer:
            "The price depends on the number of pieces and complexity. A single piece from SEK 595 (Extra Liten). A wardrobe or a couple of pieces from SEK 995 (Small). Multiple pieces from SEK 1,495 (Medium). You always see the estimated price before you book.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture assembly in Stockholm?",
          answer:
            "Yes, furniture assembly qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Can you pick up furniture from IKEA and assemble it at home in Stockholm?",
          answer:
            "Yes, we pick up flat-pack furniture from IKEA Kungens Kurva, IKEA Barkarby or another store in Stockholm, drive it to your home and assemble everything on site – all in one booking.",
        },
        {
          question: "Do you assemble furniture in all Stockholm neighborhoods?",
          answer:
            "Yes, we assemble furniture across all of Stockholm – Södermalm, Vasastan, Kungsholmen, Bromma, Farsta, Vällingby, Kista and all other neighborhoods. We also cover all surrounding municipalities such as Nacka, Solna, Huddinge, Täby and Järfälla.",
        },
        {
          question: "Is my furniture insured during assembly in Stockholm?",
          answer:
            "Yes, all jobs are insured. If anything gets damaged during furniture assembly, we cover it. Our assemblers work carefully and handle your furniture with care.",
        },
        {
          question: "Do you remove packaging after assembly in Stockholm?",
          answer:
            "Yes, we tidy up all cardboard, polystyrene and plastic wrap after assembly. We can take all packaging with us so you skip the recycling trip.",
        },
        {
          question: "Can you assemble kitchens in Stockholm?",
          answer:
            "Yes, we assemble kitchen cabinets, countertops and kitchen islands across Stockholm. We handle all carpentry – base cabinets, wall cabinets, doors, drawers and fittings. Electrical and plumbing connections need to be ordered separately.",
        },
        {
          question: "Do you assemble office furniture in Stockholm?",
          answer:
            "Yes, we assemble office furniture across Stockholm – sit-stand desks, office chairs, drawer units and meeting room furniture. We can work on evenings and weekends so assembly doesn't disrupt business operations.",
        },
        {
          question: "How long does it take to assemble a PAX wardrobe in Stockholm?",
          answer:
            "A standard PAX wardrobe with two doors takes about 45–90 minutes. Larger systems with sliding doors, drawers and fittings can take 2–3 hours. We provide an estimated time when you book.",
        },
        {
          question: "Can you disassemble furniture in Stockholm too?",
          answer:
            "Yes, we both assemble and disassemble furniture in Stockholm. Perfect if you are moving and need to take apart wardrobes, beds or desks before the move.",
        },
        {
          question: "Do you assemble furniture on evenings and weekends in Stockholm?",
          answer:
            "Yes, we offer furniture assembly in Stockholm on weekdays, evenings and weekends. Pick the time that works best when booking.",
        },
        {
          question: "In which municipalities in the Stockholm area do you offer furniture assembly?",
          answer:
            `We offer furniture assembly across the entire Stockholm area: ${stockholmKommunerText}. We also cover all neighborhoods within Stockholm city.`,
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.montering,

      pricingRows: getPackagePricingRows("montering", "en"),

      dynamicHeadings: {
        threeSteps: "Furniture assembly in Stockholm in 3 easy steps",
        step2Title: "We assemble everything",
        step2Text:
          "Our assemblers in Stockholm arrive by car with a complete toolbox. They unpack, assemble and check that everything fits perfectly. Packaging is tidied up.",
        step3Title: "Done",
        step3Desc:
          "Your furniture is assembled, adjusted and ready to use. Packaging has been cleared away and you can enjoy the result.",
        ctaHeading: "Book furniture assembly in Stockholm today",
        ctaText:
          "Describe what furniture needs assembling, enter your Stockholm address and see the price instantly. We bring all the tools – you don't need to lift a finger.",
      },
    },
  },

  {
    // Bud Stockholm — config för /bud-stockholm
    // Stadsspecifik sida för budtjänst i Stockholm.
    slug: "bud-stockholm",
    serviceCategory: "budtjänst",
    city: "stockholm",

    meta: {
      title:
        "Bud Stockholm – Boka Bud Samma Dag i Stockholm | Ungdomskraft",
      description:
        "Boka bud i Stockholm med leverans samma dag. Från kuvert till EU-pall – elmoped, bil eller skåpbil med bakgavellift. Expressbud inom 1 timme i Stockholms innerstad.",
      h1: "Bud Stockholm",
    },

    hero: {
      heading: "Boka bud i Stockholm",
      intro:
        "Snabb budleverans i hela Stockholmsregionen – från Norrmalm till Nacka, Solna till Södertälje. Vi kör allt från kuvert till pallar med anpassat fordon och levererar ofta inom ett par timmar. Expressbud i innerstaden inom 1 timme.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft bud Stockholm – ungdomar lastar paket i skåpbil för snabb budleverans i Stockholm",
    },

    featureCards: {
      headingText: "Ditt bud i Stockholm",
      sinceText: "Budfirma i Stockholm sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört bud i Stockholmsregionen åt privatpersoner, företag och eventarrangörer. Vi känner stadsdelarna, trafikmönstren och genvägarna – det gör oss snabbare än de stora budföretagen.",
      bookYourText: "Boka ditt bud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Stockholm. Du ser priset direkt och kan välja expressleverans.",
      vehicleTitleText: "Rätt fordon för Stockholmstrafiken",
      vehicleDescText:
        "Elmoped för brev och småpaket i innerstaden, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar. Vi väljer fordon efter trafikläge och godsstorlek.",
    },

    itemsSection: {
      heading: "Vanliga budleveranser i Stockholm",
      items: [
        {
          name: "Dokument & kontrakt",
          description:
            "Avtal, handlingar och juridiska dokument som ska mellan advokatbyråer, mäklare och kontor i Stockholm",
          image: "/box.webp",
        },
        {
          name: "E-handelspaket",
          description:
            "Returer, provkollektioner och expresspaket till kunder och butiker i Stockholmsområdet",
          image: "/box.webp",
        },
        {
          name: "Konst & skyltar",
          description:
            "Neonskyltar, tavlor, ljuslådor och banderoller till restauranger, gallerier och kontor i Stockholm",
          image: "/painting.webp",
        },
        {
          name: "Elektronik & teknik",
          description:
            "Servrar, skärmar och annan IT-utrustning som ska till kontor i Kista, Solna eller innerstaden",
          image: "/electronics.webp",
        },
        {
          name: "Catering & eventleveranser",
          description:
            "Mat, dryck och utrustning till event, konferenser och fester i hela Stockholmsregionen",
          image: "/appliances.webp",
        },
        {
          name: "Pallar & partileveranser",
          description:
            "EU-pallar med gods, dryckesleveranser och större volymer – bakgavellift vid behov",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Bud i Stockholm – snabba leveranser i hela regionen",
      mainText:
        "Stockholm är en stad byggd på öar, med broar, tunnlar och en trafiksituation som kräver lokal kunskap för att navigera effektivt. Det är precis därför Ungdomskraft finns. Våra budförare känner Stockholms stadsdelar utan och innan – från de trånga gränderna på Gamla stan till kontorshusen i Kista, från kajerna på Södermalm till industriområdena i Järfälla. Vi vet vilka vägar som korkar igen vid rusningstid, vilka genvägar som sparar tjugo minuter och var man faktiskt kan parkera i innerstaden. Det gör att vi levererar snabbare och smidigare än de stora, rikstäckande budföretagen som skickar förare som inte känner till staden.\n\nVårt team av unga, energiska budförare kör allt från enstaka kuvert till fulla EU-pallar i hela Stockholmsregionen. Vi anpassar fordon efter uppdraget – elmoped slinker genom rusningstrafiken på Sveavägen, liten bil tar sig snabbt till Nacka med ett par kartonger, skåpbil hanterar möbelleveranser till Bromma och stor skåpbil med bakgavellift lossar pallar vid lastbryggor i Kungens Kurva. Oavsett vad du behöver skicka har vi fordon och personal redo att köra inom Stockholmsområdet, ofta redan inom ett par timmar från bokning.",
      preparationHeading: "Förbereda ditt bud i Stockholm",
      preparationText:
        "Se till att godset är färdigpackat och redo att hämtas vid avtalad tid. Meddela oss om det finns portkod, trappor eller begränsad parkering vid hämtadressen – det är vanligt i Stockholms innerstad och vi planerar bättre med rätt information. Om du sitter på ett kontor med reception, informera receptionen om att ett bud kommer. Ange alltid kontaktperson och telefonnummer vid både hämtning och leverans. Behöver du expressleverans i Stockholm? Boka tidigt på dagen så kan vi ofta leverera inom en timme i innerstaden.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi kör bud i",
          content:
            `Vi kör bud i hela Stockholmsregionen. I innerstaden – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen – har vi flera budförare tillgängliga dagligen. Responstiden är kortast här, ofta under en timme vid expressbokning. I söderort – Farsta, Enskede, Skarpnäck, Bandhagen, Hägersten – och västerort – Bromma, Vällingby, Hässelby, Spånga – har vi god täckning tack vare närheten till ringvägarna.\n\nVi täcker även kranskommunerna: ${stockholmKommunerText}. Leveranser mellan kommuner, till exempel från en leverantör i Järfälla till ett kontor i Nacka, är vardagsmat för oss. Vi planerar alltid rutten för att minimera körtid och hålla nere kostnaden.`,
        },
        {
          heading: "Expressbud i Stockholms innerstad",
          content:
            "Stockholms innerstad är vårt snabbaste leveransområde. Med elmoped tar vi oss genom Norrmalm, Vasastan, Södermalm och Kungsholmen på minuter – utan att fastna i rusningstrafik eller behöva leta parkering. Expressbud i innerstaden levereras normalt inom 30–60 minuter. För tidskritiska dokument mellan advokatbyråer på Strandvägen, provkollektioner till showrooms på Östermalm eller akuta reservdelar till restauranger på Södermalm är expressmopeden det snabbaste alternativet. Även med bil och skåpbil erbjuder vi expressleverans i Stockholm – ditt uppdrag prioriteras framför allt annat och föraren kör raka vägen utan omvägar.",
        },
        {
          heading: "Bud till events och konferenser i Stockholm",
          content:
            "Stockholm är en av Nordens största eventstäder med arenor, konferensanläggningar och eventlokaler spridda över hela regionen – Stockholmsmässan i Älvsjö, Münchenbryggeriet på Södermalm, Grand Hôtel på Blasieholmen och Ericsson Globe i Globen. Vi kör eventleveranser dagligen och vet vad som krävs: tajt tidshållning, varsam hantering av ömtåligt material och flexibilitet att anpassa oss efter förändrade tider. Oavsett om det handlar om att leverera neonskyltar till en restaurangöppning, utrustning till en mässa eller catering till en företagskonferens – vi löser det. Med bakgavellift hanterar vi pallar med dryck, scendekor och annat tungt eventgods direkt vid lastbrygga.",
        },
        {
          heading: "Varför boka bud i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal Stockholmskunskap –",
              text: "Vi känner trafikmönster, genvägar och parkeringsmöjligheter i hela Stockholmsregionen. Det gör leveranserna snabbare och billigare.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har budförare redo i Stockholm dagligen, inklusive kvällar och helger. Expressbud i innerstaden ofta inom 30–60 minuter.",
            },
            {
              label: "Anpassat fordon –",
              text: "Elmoped i city, bil för mellanstora leveranser, skåpbil med bakgavellift för pallar. Alltid rätt fordon för Stockholmstrafiken.",
            },
            {
              label: "Fast pris direkt –",
              text: "Du ser priset vid bokning. Inga dolda avgifter, inga tilläggsavgifter för innerstaden.",
            },
            {
              label: "Stödjer unga –",
              text: "Varje bud du bokar ger en ung Stockholmare möjlighet att jobba och växa. Budfirma med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur snabbt kan ni leverera bud i Stockholm?",
        answer:
          "Med expressleverans kan vi ofta leverera inom 30–60 minuter i Stockholms innerstad. Standardleverans samma dag innebär leverans inom 2–4 timmar beroende på avstånd inom Stockholmsregionen.",
      },
      {
        question: "Vad kostar det att skicka bud i Stockholm?",
        answer:
          "Priset beror på fordon, avstånd och tid. Extra Liten (elmoped) från 595 kr, Small (liten bil) från 995 kr, Medium (skåpbil) från 1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Vilka stadsdelar i Stockholm kör ni bud i?",
        answer:
          `Vi kör bud i hela Stockholmsregionen – innerstaden, söderort, västerort och alla kranskommuner: ${stockholmKommunerText}. Ingen del av Stockholm är utanför vårt leveransområde.`,
      },
      {
        question: "Kan ni köra expressbud i Stockholms innerstad?",
        answer:
          "Ja, expressbud i innerstaden är vår snabbaste tjänst. Med elmoped levererar vi dokument, nycklar och småpaket inom 30–60 minuter i Norrmalm, Södermalm, Vasastan, Östermalm och Kungsholmen.",
      },
      {
        question: "Vilka fordon har ni i Stockholm?",
        answer:
          "Vi har el-driven moped för brev och småpaket i innerstaden, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar och tungt gods.",
      },
      {
        question: "Kan ni leverera till events i Stockholm?",
        answer:
          "Ja, vi kör dagligen eventleveranser i Stockholm – catering, utrustning, skyltar och dryck till Stockholmsmässan, Münchenbryggeriet, Grand Hôtel och andra eventlokaler. Tajt tidshållning är vår specialitet.",
      },
      {
        question: "Kör ni bud till kranskommuner som Nacka och Solna?",
        answer:
          `Ja, vi kör bud i hela Stockholmsregionen inklusive alla kranskommuner. Leveranser mellan kommuner, till exempel från Järfälla till Nacka, är vardagsmat för oss.`,
      },
      {
        question: "Kan jag boka bud i Stockholm samma dag?",
        answer:
          "Ja, de flesta buduppdrag i Stockholm hämtas och levereras samma dag. Vi bekräftar upphämtning och leveranstid direkt vid bokning.",
      },
      {
        question: "Erbjuder ni företagsavtal för bud i Stockholm?",
        answer:
          "Ja, vi erbjuder avtalspriser för Stockholmsbaserade företag med återkommande leveransbehov. Rabatterade priser, fasta hämtningstider och samlad fakturering.",
      },
      {
        question: "Kan ni köra en EU-pall i Stockholm?",
        answer:
          "Ja, vi har stor skåpbil med bakgavellift som hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs på mottagande sida.",
      },
      {
        question: "Hanterar ni ömtåliga föremål i Stockholm?",
        answer:
          "Ja, vi kör neonskyltar, konst, elektronik, tårtor och andra ömtåliga föremål i Stockholm dagligen. Vi rekommenderar ordentlig förpackning, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kör ni bud på kvällar och helger i Stockholm?",
        answer:
          "Ja, vi kör bud i Stockholm även på kvällar och helger. Tillgängligheten kan variera, men du ser lediga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag bud i Stockholm?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill skicka, ange hämtadress och leveransadress i Stockholm, välj tid och se priset direkt.",
      },
      {
        question: "Är mina saker försäkrade under budtransporten?",
        answer:
          "Ja, alla budleveranser i Stockholm är försäkrade under transporten. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg.",
      },
      {
        question: "Vad är skillnaden mellan standardbud och expressbud i Stockholm?",
        answer:
          "Standardbud levereras inom 2–4 timmar. Expressbud prioriteras direkt och levereras så snabbt som möjligt – ofta inom 30–60 minuter i Stockholms innerstad. Expressbud kostar lite mer men sparar tid.",
      },
      {
        question: "Kan budet göra flera stopp i Stockholm?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Perfekt om du behöver hämta från en leverantör i Solna och leverera till två kontor i innerstaden.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Bud i Stockholm i 3 steg",
      step2Text:
        "Vår budförare hämtar ditt gods i Stockholm och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt till rätt adress i Stockholm. Mottagaren bekräftar leveransen och uppdraget är klart.",
      ctaHeading: "Skicka bud i Stockholm idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Stockholm. Du ser priset direkt och kan välja expressleverans.",
    },

    en: {
      slug: "courier-stockholm",
      meta: {
        title:
          "Courier Stockholm – Same Day Delivery in Stockholm | Ungdomskraft",
        description:
          "Book a courier in Stockholm with same-day delivery. From envelopes to EU pallets – electric moped, car or van with tail lift. Express delivery within 1 hour in central Stockholm.",
        h1: "Courier Stockholm",
      },

      hero: {
        heading: "Book a courier in Stockholm",
        intro:
          "Fast courier delivery across the Stockholm region – from Norrmalm to Nacka, Solna to Södertälje. We deliver everything from envelopes to pallets with the right vehicle and often within a couple of hours. Express delivery in the city center within 1 hour.",
        heroImageAlt:
          "Ungdomskraft courier Stockholm – young workers loading packages into delivery van in Stockholm",
      },

      featureCards: {
        headingText: "Your Stockholm courier",
        sinceText: "Courier service in Stockholm since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running courier deliveries across the Stockholm region for individuals, businesses and event organizers. We know the neighborhoods, traffic patterns and shortcuts – making us faster than the big national courier companies.",
        bookYourText: "Book your courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses in Stockholm. You see the price instantly and can choose express delivery.",
        vehicleTitleText: "The right vehicle for Stockholm traffic",
        vehicleDescText:
          "Electric moped for letters and small packages in the city center, small car for medium deliveries, van for larger goods, and large van with tail lift for pallets. We choose the vehicle based on traffic and cargo size.",
      },

      itemsSection: {
        heading: "Common courier deliveries in Stockholm",
        items: [
          {
            name: "Documents & contracts",
            description:
              "Agreements, legal documents and paperwork between law firms, real estate agents and offices in Stockholm",
            image: "/box.webp",
          },
          {
            name: "E-commerce packages",
            description:
              "Returns, sample collections and express packages to customers and stores in the Stockholm area",
            image: "/box.webp",
          },
          {
            name: "Art & signage",
            description:
              "Neon signs, paintings, light boxes and banners to restaurants, galleries and offices in Stockholm",
            image: "/painting.webp",
          },
          {
            name: "Electronics & tech",
            description:
              "Servers, monitors and other IT equipment to offices in Kista, Solna or the city center",
            image: "/electronics.webp",
          },
          {
            name: "Catering & event deliveries",
            description:
              "Food, beverages and equipment for events, conferences and parties across the Stockholm region",
            image: "/appliances.webp",
          },
          {
            name: "Pallets & bulk deliveries",
            description:
              "EU pallets, beverage deliveries and larger volumes – tail lift available when needed",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Courier in Stockholm – fast deliveries across the region",
        mainText:
          "Stockholm is a city built on islands, with bridges, tunnels and traffic conditions that demand local knowledge to navigate efficiently. That is exactly why Ungdomskraft exists. Our couriers know Stockholm's neighborhoods inside and out – from the narrow alleys of Gamla Stan to the office parks in Kista, from the waterfronts of Södermalm to the industrial areas in Järfälla. We know which roads get congested at rush hour, which shortcuts save twenty minutes and where you can actually park in the city center. This means we deliver faster and more smoothly than the big nationwide courier companies that send drivers unfamiliar with the city.\n\nOur team of young, energetic couriers delivers everything from single envelopes to full EU pallets across the Stockholm region. We match the vehicle to the job – electric moped weaves through rush hour traffic on Sveavägen, a small car reaches Nacka quickly with a couple of boxes, a van handles furniture deliveries to Bromma, and a large van with tail lift unloads pallets at loading docks in Kungens Kurva. Whatever you need to send, we have vehicles and staff ready to go within the Stockholm area, often within just a couple of hours from booking.",
        preparationHeading: "Preparing your Stockholm courier job",
        preparationText:
          "Make sure your goods are packed and ready for pickup at the agreed time. Let us know about entry codes, stairs or limited parking at the pickup address – this is common in central Stockholm and we plan better with the right information. If you are in an office with a reception desk, let them know a courier is coming. Always provide a contact person and phone number at both pickup and delivery. Need express delivery in Stockholm? Book early in the day and we can often deliver within one hour in the city center.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover",
            content:
              `We deliver across the entire Stockholm region. In the city center – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen – we have multiple couriers available daily. Response times are shortest here, often under one hour with express booking. In the southern suburbs – Farsta, Enskede, Skarpnäck, Bandhagen, Hägersten – and the western suburbs – Bromma, Vällingby, Hässelby, Spånga – we have strong coverage thanks to proximity to the ring roads.\n\nWe also cover all surrounding municipalities: ${stockholmKommunerText}. Deliveries between municipalities, for example from a supplier in Järfälla to an office in Nacka, are everyday business for us. We always plan the route to minimize driving time and keep costs down for you.`,
          },
          {
            heading: "Express courier in central Stockholm",
            content:
              "Central Stockholm is our fastest delivery zone. By electric moped, we move through Norrmalm, Vasastan, Södermalm and Kungsholmen in minutes – without getting stuck in rush hour traffic or searching for parking. Express deliveries in the city center are typically completed within 30–60 minutes. For time-critical documents between law firms on Strandvägen, sample collections to showrooms on Östermalm or urgent spare parts to restaurants on Södermalm, the express moped is the fastest option. We also offer express delivery by car and van in Stockholm – your job gets top priority and the driver takes the most direct route.",
          },
          {
            heading: "Event and conference deliveries in Stockholm",
            content:
              "Stockholm is one of the Nordic region's biggest event cities, with arenas, conference venues and event spaces spread across the region – Stockholmsmässan in Älvsjö, Münchenbryggeriet on Södermalm, Grand Hôtel on Blasieholmen and Ericsson Globe in Globen. We run event deliveries daily and know what it takes: tight scheduling, careful handling of fragile materials and flexibility to adapt to changing timelines. Whether it is neon signs for a restaurant opening, equipment for a trade fair or catering for a corporate conference – we handle it. With our tail lift, we manage pallets of beverages, stage decor and other heavy event goods directly at loading docks.",
          },
          {
            heading: "Why book a courier in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local Stockholm expertise –",
                text: "We know traffic patterns, shortcuts and parking options across the Stockholm region. This makes deliveries faster and more affordable.",
              },
              {
                label: "Fast availability –",
                text: "We have couriers ready in Stockholm daily, including evenings and weekends. Express delivery in the city center often within 30–60 minutes.",
              },
              {
                label: "Right vehicle –",
                text: "Electric moped in the city, car for medium deliveries, van with tail lift for pallets. Always the right vehicle for Stockholm traffic.",
              },
              {
                label: "Fixed price upfront –",
                text: "You see the price when booking. No hidden fees, no surcharges for the city center.",
              },
              {
                label: "Supporting young people –",
                text: "Every courier you book gives a young Stockholmer the opportunity to work and grow. Courier service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How fast can you deliver a courier in Stockholm?",
          answer:
            "With express delivery, we can often deliver within 30–60 minutes in central Stockholm. Standard same-day delivery means delivery within 2–4 hours depending on distance within the Stockholm region.",
        },
        {
          question: "How much does courier service in Stockholm cost?",
          answer:
            "The price depends on vehicle, distance and time. Extra Liten (electric moped) from SEK 595, Small (small car) from SEK 995, Medium (van) from SEK 1,495. You see the price instantly when booking.",
        },
        {
          question: "Which Stockholm neighborhoods do you cover?",
          answer:
            `We deliver across the entire Stockholm region – the city center, southern and western suburbs, and all surrounding municipalities: ${stockholmKommunerText}. No part of Stockholm is outside our delivery area.`,
        },
        {
          question: "Can you do express delivery in central Stockholm?",
          answer:
            "Yes, express delivery in the city center is our fastest service. By electric moped, we deliver documents, keys and small packages within 30–60 minutes in Norrmalm, Södermalm, Vasastan, Östermalm and Kungsholmen.",
        },
        {
          question: "What vehicles do you have in Stockholm?",
          answer:
            "We have electric mopeds for letters and small packages in the city center, small cars for medium deliveries, vans for larger goods, and large vans with tail lift for pallets and heavy cargo.",
        },
        {
          question: "Can you deliver to events in Stockholm?",
          answer:
            "Yes, we run daily event deliveries in Stockholm – catering, equipment, signage and beverages to Stockholmsmässan, Münchenbryggeriet, Grand Hôtel and other event venues. Tight scheduling is our specialty.",
        },
        {
          question: "Do you deliver to suburbs like Nacka and Solna?",
          answer:
            "Yes, we deliver across the entire Stockholm region including all surrounding municipalities. Deliveries between municipalities, for example from Järfälla to Nacka, are everyday business for us.",
        },
        {
          question: "Can I book a same-day courier in Stockholm?",
          answer:
            "Yes, most courier jobs in Stockholm are picked up and delivered the same day. We confirm pickup and delivery time immediately when you book.",
        },
        {
          question: "Do you offer business contracts in Stockholm?",
          answer:
            "Yes, we offer contract pricing for Stockholm-based businesses with recurring delivery needs. Discounted rates, scheduled pickups and consolidated invoicing.",
        },
        {
          question: "Can you deliver a EU pallet in Stockholm?",
          answer:
            "Yes, we have a large van with tail lift that handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed at the receiving end.",
        },
        {
          question: "Do you handle fragile items in Stockholm?",
          answer:
            "Yes, we deliver neon signs, artwork, electronics, cakes and other fragile items in Stockholm daily. We recommend proper packaging, but our couriers handle everything with care.",
        },
        {
          question: "Do you operate evenings and weekends in Stockholm?",
          answer:
            "Yes, we offer courier service in Stockholm on evenings and weekends. Availability may vary, but you can see available times when booking.",
        },
        {
          question: "How do I book a courier in Stockholm?",
          answer:
            "You book directly on our website. Describe what you want to send, enter pickup and delivery addresses in Stockholm, choose a time and see the price instantly.",
        },
        {
          question: "Are my goods insured during the delivery?",
          answer:
            "Yes, all courier deliveries in Stockholm are insured during transport. For fragile or valuable items, we recommend letting us know in advance.",
        },
        {
          question: "What is the difference between standard and express courier in Stockholm?",
          answer:
            "Standard courier delivers within 2–4 hours. Express courier is prioritized immediately and delivered as fast as possible – often within 30–60 minutes in central Stockholm. Express costs a bit more but saves time.",
        },
        {
          question: "Can the courier make multiple stops in Stockholm?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Perfect if you need to pick up from a supplier in Solna and deliver to two offices in the city center.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Courier in Stockholm in 3 steps",
        step2Text:
          "Our courier picks up your goods in Stockholm and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived at the right address in Stockholm. The recipient confirms delivery and the job is done.",
        ctaHeading: "Book a courier in Stockholm today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses in Stockholm. You see the price instantly and can choose express delivery.",
      },
    },
  },

  {
    // Budbil Stockholm — config för /budbil-stockholm
    // Stadsspecifik sida för budbil med chaufför i Stockholm.
    slug: "budbil-stockholm",
    serviceCategory: "budtjänst",
    city: "stockholm",

    meta: {
      title:
        "Budbil Stockholm – Boka Budbil med Chaufför Samma Dag | Ungdomskraft",
      description:
        "Boka budbil i Stockholm med chaufför och bärhjälp. Skåpbil med bakgavellift vid behov. Leverans samma dag i hela Stockholmsregionen. Budbil sedan 2014.",
      h1: "Budbil Stockholm",
    },

    hero: {
      heading: "Boka budbil i Stockholm",
      intro:
        "Behöver du en budbil med chaufför i Stockholm som hämtar och levererar samma dag? Vi kör allt från kartonger till fullastade pallar i hela Stockholmsregionen. Du får rätt fordon, starka armar och snabb leverans – utan att själv lyfta ett finger.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft budbil Stockholm – ungdomar lastar paket i skåpbil för snabb leverans i Stockholm",
    },

    featureCards: {
      headingText: "Din budbil i Stockholm",
      sinceText: "Budbil i Stockholm sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört budbil i Stockholmsregionen åt privatpersoner, företag och eventarrangörer. Våra unga förare känner varje stadsdel, vet var man parkerar i innerstaden och navigerar Stockholmstrafiken snabbare än de stora budföretagen.",
      bookYourText: "Boka din budbil",
      bookDescText:
        "Beskriv vad som ska hämtas, ange adresser i Stockholm och se priset direkt. Vi matchar rätt budbil efter ditt gods.",
      vehicleTitleText: "Rätt budbil för Stockholmstrafiken",
      vehicleDescText:
        "Liten bil för snabba leveranser i innerstaden, skåpbil för större volymer till Bromma eller Nacka, stor skåpbil med bakgavellift för pallar till Kungens Kurva. Du betalar bara för den kapacitet du behöver.",
    },

    itemsSection: {
      heading: "Det här kör vi med budbil i Stockholm",
      items: [
        {
          name: "Kontorsflytt & inventarier",
          description:
            "Kontorsstolar, skrivbord, skärmar och arkivskåp mellan kontor i Kista, Solna, Sundbyberg och innerstaden",
          image: "/furniture.webp",
        },
        {
          name: "Blocket- & Marketplace-köp",
          description:
            "Möbler, elektronik och prylar du köpt av privatpersoner i Stockholm – vi hämtar och kör hem till dig",
          image: "/box.webp",
        },
        {
          name: "Restaurangleveranser",
          description:
            "Storsändningar av livsmedel, dryck och utrustning till restauranger på Södermalm, Östermalm och Kungsholmen",
          image: "/appliances.webp",
        },
        {
          name: "Eventleveranser",
          description:
            "Scendekor, ljud, belysning och catering till event på Stockholmsmässan, Münchenbryggeriet och Grand Hôtel",
          image: "/painting.webp",
        },
        {
          name: "E-handel & returer",
          description:
            "Returpaket, provkollektioner och expressleveranser till e-handlare och butiker i Stockholmsområdet",
          image: "/box.webp",
        },
        {
          name: "Pallar & tungt gods",
          description:
            "EU-pallar med varor, maskindelar och tungt gods – budbil med bakgavellift till lastbryggor i hela regionen",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Budbil i Stockholm – fordon, chaufför och bärhjälp i hela regionen",
      mainText:
        "Att köra budbil i Stockholm kräver mer än bara ett fordon. Det kräver lokalkännedom – vetskap om att Essingeleden korkar igen varje eftermiddag, att det är lättare att nå Södermalm via Skansbron än Slussen under rusningstid, och att parkering på Östermalm är en konst i sig. Ungdomskrafts budbilsförare har den kunskapen. De kör i Stockholmsregionen dagligen och vet exakt hur man tar sig fram snabbast oavsett tid på dygnet.\n\nMed Ungdomskraft får du mer än en budbil – du får ett komplett team. Våra unga, starka förare lastar, bär uppför trappor i trånga sekelskifteshus på Vasastan, navigerar smala hissar i funkisbyggnader på Gärdet och levererar hela vägen in genom dörren. Du behöver varken hyra bil, be kompisar om hjälp eller ta ledigt från jobbet. Beskriv vad du behöver transportera så matchar vi rätt budbil: liten bil för snabba leveranser mellan kontor i innerstaden, skåpbil för möbler och kartonger till Bromma eller Lidingö, och stor skåpbil med bakgavellift för pallar och tungt gods till industriområden i Järfälla eller Kungens Kurva. Du ser priset direkt innan du bokar – inga dolda avgifter.",
      preparationHeading: "Förbered din budbil-leverans i Stockholm",
      preparationText:
        "Se till att godset är färdigpackat och tillgängligt vid hämtning. I Stockholms innerstad är parkering ofta begränsad – meddela oss om det finns innergård, garageinfart eller lastzon vid hämtadressen. Det sparar tid och håller nere kostnaden. Möbler som ska isärtagas bör vara demonterade i förväg. Om du bor i lägenhet, kontrollera om hissen tar det som ska transporteras – våra förare bär givetvis i trappor om det behövs, men det är bra att veta i förväg. Ange portkod, våning och kontaktperson vid båda adresserna.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi kör budbil i",
          content:
            `Vi kör budbil i hela Stockholmsregionen. I innerstaden – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen – har vi budbilsförare tillgängliga dagligen med kort responstid. I söderort – Farsta, Enskede, Hägersten, Skarpnäck, Älvsjö – och västerort – Bromma, Vällingby, Hässelby, Spånga – når vi er snabbt via ringvägarna.\n\nVi täcker även alla kranskommuner: ${stockholmKommunerText}. Leveranser tvärs över regionen, till exempel en kontorsflytt från Sundbyberg till Nacka eller en palleverans från Järfälla till Tyresö, är vardagsmat. Vi planerar alltid den snabbaste rutten.`,
        },
        {
          heading: "Budbil samma dag i Stockholm",
          content:
            "De flesta som söker budbil i Stockholm behöver leveransen idag, inte imorgon. Därför erbjuder vi budbil med leverans samma dag som standard i hela Stockholmsregionen. Du bokar, vi bekräftar och vår chaufför är på plats inom ett par timmar. Behöver du det snabbare? Välj expressleverans – ditt uppdrag prioriteras direkt och budbilen åker raka vägen utan omvägar. I centrala Stockholm kan expressleverans med liten bil innebära 30–60 minuter. Med skåpbil räkna med en till två timmar beroende på avstånd och trafikläge.",
        },
        {
          heading: "Budbil för Stockholmsföretag – avtal och fasta flöden",
          content:
            "Driver du ett företag i Stockholm med regelbundna leveransbehov? Vi erbjuder företagsavtal med rabatterade budbilspriser, schemalagda hämtningar och dedikerade förare som lär känna ditt företag. Advokatbyråer vid Strandvägen som skickar dokument dagligen, restauranger på Södermalm med leverantörsleveranser, e-handlare i Kista med returer och arkitektkontor i Hammarby Sjöstad med materialprover – alla får ett skräddarsytt upplägg. Med ett företagsavtal slipper du boka varje enskilt uppdrag. Budbilen kommer enligt schema, och du får en samlad faktura varje månad.",
        },
        {
          heading: "Varför boka budbil i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal Stockholmskunskap –",
              text: "Våra förare kör i Stockholm dagligen och känner trafikmönster, parkeringsmöjligheter och genvägar i varje stadsdel. Det ger snabbare leveranser och lägre kostnad.",
            },
            {
              label: "Bärhjälp ingår alltid –",
              text: "Våra unga förare lastar, bär uppför trappor och levererar hela vägen in. Du behöver inte lyfta ett finger – oavsett om det är en etta på Södermalm eller ett kontor på plan 8 i Kista.",
            },
            {
              label: "Rätt budbil för uppdraget –",
              text: "Liten bil för snabba stadsärenden, skåpbil för möbler, stor skåpbil med bakgavellift för pallar. Alltid rätt fordon för Stockholmstrafiken.",
            },
            {
              label: "Fast pris direkt –",
              text: "Du ser priset vid bokning. Inga dolda avgifter, inga tilläggsavgifter för innerstaden eller kranskommuner.",
            },
            {
              label: "Stödjer unga Stockholmare –",
              text: "Varje budbil du bokar ger en ung person chansen att jobba och växa. Budbil med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att boka budbil i Stockholm?",
        answer:
          "Priset beror på fordon, avstånd och tid. Liten bil från 995 kr, skåpbil från 1 495 kr, stor skåpbil med bakgavellift från 2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Hur snabbt kan budbilen vara på plats i Stockholm?",
        answer:
          "Med standardleverans är budbilen på plats inom 2–4 timmar. Med expressleverans prioriteras ditt uppdrag direkt – ofta inom 30–60 minuter i centrala Stockholm.",
      },
      {
        question: "Vilka stadsdelar i Stockholm kör ni budbil i?",
        answer:
          `Vi kör budbil i hela Stockholmsregionen – innerstaden, söderort, västerort och alla kranskommuner: ${stockholmKommunerText}. Ingen del av Stockholm är utanför vårt leveransområde.`,
      },
      {
        question: "Ingår bärhjälp med budbilen i Stockholm?",
        answer:
          "Ja, bärhjälp ingår alltid. Våra förare lastar, bär uppför trappor och levererar hela vägen in – oavsett om det är en walk-up på Södermalm eller ett kontorshus i Solna.",
      },
      {
        question: "Kan budbilen ta en EU-pall i Stockholm?",
        answer:
          "Ja, vår stora budbil har bakgavellift och hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs på mottagande sida.",
      },
      {
        question: "Kan jag boka budbil i Stockholm samma dag?",
        answer:
          "Ja, de flesta budbil-uppdrag i Stockholm hämtas och levereras samma dag. Vi bekräftar tid direkt vid bokning.",
      },
      {
        question: "Vilka fordon har ni som budbil i Stockholm?",
        answer:
          "Liten bil för snabba leveranser i innerstaden, skåpbil för större volymer och möbler, stor skåpbil med bakgavellift för pallar och tungt gods.",
      },
      {
        question: "Kör budbilen till kranskommuner som Nacka och Solna?",
        answer:
          "Ja, vi kör budbil i hela Stockholmsregionen inklusive alla kranskommuner. Leveranser från exempelvis Sundbyberg till Nacka är vardag för oss.",
      },
      {
        question: "Kan budbilen hämta Blocket-köp i Stockholm?",
        answer:
          "Ja, vi hämtar möbler, elektronik och annat du köpt av privatpersoner i Stockholm. Beskriv vad som ska hämtas, ange adresser och vi kör det hem till dig samma dag.",
      },
      {
        question: "Erbjuder ni företagsavtal för budbil i Stockholm?",
        answer:
          "Ja, vi erbjuder avtalspriser för Stockholmsföretag med återkommande behov av budbil. Rabatterade priser, fasta hämtningstider och samlad fakturering.",
      },
      {
        question: "Hanterar budbilen ömtåliga föremål i Stockholm?",
        answer:
          "Ja, vi transporterar neonskyltar, konst, elektronik och andra ömtåliga saker i Stockholm dagligen. Vi rekommenderar ordentlig förpackning, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kör budbilen på kvällar och helger i Stockholm?",
        answer:
          "Ja, vi erbjuder budbil i Stockholm även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag budbil i Stockholm?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill transportera, ange hämtadress och leveransadress i Stockholm, och se priset direkt.",
      },
      {
        question: "Är mina saker försäkrade i budbilen?",
        answer:
          "Ja, alla transporter med budbil i Stockholm är försäkrade. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg.",
      },
      {
        question: "Vad är skillnaden mellan budbil och vanligt bud i Stockholm?",
        answer:
          "Ett vanligt bud kan skickas med elmoped eller liten bil för lätta leveranser. En budbil innebär ett större fordon – skåpbil eller skåpbil med bakgavellift – med chaufför och bärhjälp för tyngre gods.",
      },
      {
        question: "Kan budbilen göra flera stopp i Stockholm?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Perfekt om du behöver hämta från en leverantör i Solna och leverera till kontor i innerstaden och Nacka.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Budbil i Stockholm i 3 steg",
      step2Text:
        "Vår chaufför hämtar ditt gods i Stockholm, lastar budbilen och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt. Chauffören har burit in och ställt på plats i Stockholm – uppdraget är klart.",
      ctaHeading: "Boka budbil i Stockholm idag",
      ctaText:
        "Beskriv vad du vill transportera, ange hämtadress och leveransadress i Stockholm. Du ser priset direkt och kan välja expressleverans.",
    },

    en: {
      slug: "delivery-van-stockholm",
      meta: {
        title:
          "Delivery Van Stockholm – Book a Van with Driver Same Day | Ungdomskraft",
        description:
          "Book a delivery van with driver in Stockholm. Van with tail lift available. Same-day delivery across the Stockholm region with carrying help included.",
        h1: "Delivery Van Stockholm",
      },

      hero: {
        heading: "Book a delivery van in Stockholm",
        intro:
          "Need a delivery van with a driver in Stockholm who picks up and delivers the same day? We handle everything from boxes to fully loaded pallets across the Stockholm region. You get the right vehicle, strong arms and fast delivery – without lifting a finger.",
        heroImageAlt:
          "Ungdomskraft delivery van Stockholm – young workers loading packages for same-day delivery in Stockholm",
      },

      featureCards: {
        headingText: "Your delivery van in Stockholm",
        sinceText: "Delivery van in Stockholm since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running delivery vans across the Stockholm region for individuals, businesses and event organizers. Our young drivers know every neighborhood, where to park downtown, and navigate Stockholm traffic faster than the big courier companies.",
        bookYourText: "Book your delivery van",
        bookDescText:
          "Describe what needs to be picked up, enter addresses in Stockholm and see the price instantly. We match the right van to your cargo.",
        vehicleTitleText: "The right van for Stockholm traffic",
        vehicleDescText:
          "Small car for quick inner-city deliveries, van for larger volumes to Bromma or Nacka, large van with tail lift for pallets to Kungens Kurva. You only pay for the capacity you need.",
      },

      itemsSection: {
        heading: "What we deliver by van in Stockholm",
        items: [
          {
            name: "Office moves & inventory",
            description:
              "Office chairs, desks, monitors and filing cabinets between offices in Kista, Solna, Sundbyberg and the city center",
            image: "/furniture.webp",
          },
          {
            name: "Blocket & Marketplace purchases",
            description:
              "Furniture, electronics and items you bought from private sellers in Stockholm – we pick up and deliver to your door",
            image: "/box.webp",
          },
          {
            name: "Restaurant deliveries",
            description:
              "Bulk food, beverages and equipment to restaurants on Södermalm, Östermalm and Kungsholmen",
            image: "/appliances.webp",
          },
          {
            name: "Event deliveries",
            description:
              "Stage decor, sound, lighting and catering for events at Stockholmsmässan, Münchenbryggeriet and Grand Hôtel",
            image: "/painting.webp",
          },
          {
            name: "E-commerce & returns",
            description:
              "Return packages, sample collections and express deliveries to e-commerce businesses and stores in the Stockholm area",
            image: "/box.webp",
          },
          {
            name: "Pallets & heavy goods",
            description:
              "EU pallets, machine parts and heavy cargo – delivery van with tail lift to loading docks across the region",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery van in Stockholm – vehicle, driver and carrying help across the region",
        mainText:
          "Driving a delivery van in Stockholm requires more than just a vehicle. It requires local knowledge – knowing that Essingeleden gets congested every afternoon, that reaching Södermalm via Skansbron is faster than Slussen at rush hour, and that parking on Östermalm is an art form. Ungdomskraft's delivery van drivers have that knowledge. They drive across the Stockholm region daily and know exactly how to get there fastest regardless of the time of day.\n\nWith Ungdomskraft, you get more than a delivery van – you get a complete team. Our young, strong drivers load, carry up stairs in narrow turn-of-the-century buildings on Vasastan, navigate tight elevators in functionalist buildings on Gärdet, and deliver all the way through the door. You don't need to rent a van, ask friends for help or take time off work. Describe what you need transported and we match the right van: small car for quick deliveries between offices in the city center, van for furniture and boxes to Bromma or Lidingö, and large van with tail lift for pallets and heavy goods to industrial areas in Järfälla or Kungens Kurva. You see the price upfront before you book – no hidden fees.",
        preparationHeading: "Prepare your delivery van pickup in Stockholm",
        preparationText:
          "Make sure goods are packed and accessible at pickup. In central Stockholm, parking is often limited – let us know if there is a courtyard, garage entrance or loading zone at the pickup address. This saves time and keeps costs down. Furniture that needs disassembly should be taken apart in advance. If you live in an apartment, check whether the elevator fits what needs to be transported – our drivers will carry up stairs if needed, but it helps to know upfront. Provide entry code, floor number and contact person at both addresses.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover",
            content:
              `We operate delivery vans across the entire Stockholm region. In the city center – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen – we have van drivers available daily with short response times. In the southern suburbs – Farsta, Enskede, Hägersten, Skarpnäck, Älvsjö – and the western suburbs – Bromma, Vällingby, Hässelby, Spånga – we reach you quickly via the ring roads.\n\nWe also cover all surrounding municipalities: ${stockholmKommunerText}. Cross-region deliveries, for example an office move from Sundbyberg to Nacka or a pallet delivery from Järfälla to Tyresö, are everyday business. We always plan the fastest route.`,
          },
          {
            heading: "Same-day delivery van in Stockholm",
            content:
              "Most people looking for a delivery van in Stockholm need the delivery today, not tomorrow. That is why we offer same-day delivery as standard across the Stockholm region. You book, we confirm, and our driver is on site within a couple of hours. Need it faster? Choose express delivery – your job gets immediate priority and the van heads straight to the pickup address. In central Stockholm, express delivery by small car can mean 30–60 minutes. With a van, expect one to two hours depending on distance and traffic.",
          },
          {
            heading: "Delivery van for Stockholm businesses – contracts and regular runs",
            content:
              "Running a business in Stockholm with regular delivery needs? We offer business agreements with discounted delivery van rates, scheduled pickups and dedicated drivers who know your company. Law firms on Strandvägen sending documents daily, restaurants on Södermalm with supplier deliveries, e-commerce businesses in Kista handling returns, and architecture studios in Hammarby Sjöstad with material samples – everyone gets a tailored solution. With a business agreement, you skip booking each individual job. The van arrives on schedule, and you receive one invoice per month.",
          },
          {
            heading: "Why book a delivery van in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Local Stockholm knowledge –",
                text: "Our drivers operate in Stockholm daily and know traffic patterns, parking options and shortcuts in every neighborhood. This means faster deliveries and lower cost.",
              },
              {
                label: "Carrying help always included –",
                text: "Our young drivers load, carry up stairs and deliver all the way inside. You don't need to lift a finger – whether it is a walk-up on Södermalm or an office on the 8th floor in Kista.",
              },
              {
                label: "Right van for the job –",
                text: "Small car for quick city errands, van for furniture, large van with tail lift for pallets. Always the right vehicle for Stockholm traffic.",
              },
              {
                label: "Fixed price upfront –",
                text: "You see the price when booking. No hidden fees, no surcharges for the city center or surrounding municipalities.",
              },
              {
                label: "Supporting young Stockholmers –",
                text: "Every delivery van you book gives a young person the chance to work and grow. Delivery van service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How much does a delivery van in Stockholm cost?",
          answer:
            "The price depends on vehicle, distance and time. Small car from SEK 995, van from SEK 1,495, large van with tail lift from SEK 2,495. You see the price instantly when booking.",
        },
        {
          question: "How fast can the delivery van arrive in Stockholm?",
          answer:
            "With standard delivery, the van arrives within 2–4 hours. With express delivery, your job is prioritized immediately – often within 30–60 minutes in central Stockholm.",
        },
        {
          question: "Which Stockholm neighborhoods do you cover?",
          answer:
            `We operate delivery vans across the entire Stockholm region – the city center, southern and western suburbs, and all surrounding municipalities: ${stockholmKommunerText}. No part of Stockholm is outside our area.`,
        },
        {
          question: "Is carrying help included with the delivery van?",
          answer:
            "Yes, carrying help is always included. Our drivers load, carry up stairs and deliver all the way inside – whether it is a walk-up on Södermalm or an office building in Solna.",
        },
        {
          question: "Can the delivery van handle a EU pallet in Stockholm?",
          answer:
            "Yes, our large van has a tail lift and handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed at the receiving end.",
        },
        {
          question: "Can I book a same-day delivery van in Stockholm?",
          answer:
            "Yes, most delivery van jobs in Stockholm are picked up and delivered the same day. We confirm the time immediately when you book.",
        },
        {
          question: "What vehicles do you use as delivery vans in Stockholm?",
          answer:
            "Small car for quick deliveries in the city center, van for larger volumes and furniture, large van with tail lift for pallets and heavy goods.",
        },
        {
          question: "Do you deliver to suburbs like Nacka and Solna?",
          answer:
            "Yes, we operate delivery vans across the entire Stockholm region including all surrounding municipalities. Deliveries from Sundbyberg to Nacka are everyday business for us.",
        },
        {
          question: "Can the van pick up Blocket purchases in Stockholm?",
          answer:
            "Yes, we pick up furniture, electronics and other items you have bought from private sellers in Stockholm. Describe what needs picking up, enter addresses and we deliver it to your door the same day.",
        },
        {
          question: "Do you offer business contracts for delivery vans in Stockholm?",
          answer:
            "Yes, we offer contract pricing for Stockholm businesses with recurring delivery van needs. Discounted rates, scheduled pickups and consolidated invoicing.",
        },
        {
          question: "Do you handle fragile items in Stockholm?",
          answer:
            "Yes, we transport neon signs, artwork, electronics and other fragile items in Stockholm daily. We recommend proper packaging, but our drivers handle everything with care.",
        },
        {
          question: "Do you operate evenings and weekends in Stockholm?",
          answer:
            "Yes, we offer delivery van service in Stockholm on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "How do I book a delivery van in Stockholm?",
          answer:
            "You book directly on our website. Describe what you want to transport, enter pickup and delivery addresses in Stockholm, and see the price instantly.",
        },
        {
          question: "Are my goods insured in the delivery van?",
          answer:
            "Yes, all delivery van transports in Stockholm are insured. For fragile or valuable items, we recommend letting us know in advance.",
        },
        {
          question: "What is the difference between a delivery van and a regular courier in Stockholm?",
          answer:
            "A regular courier can be sent by electric moped or small car for light deliveries. A delivery van means a larger vehicle – van or van with tail lift – with a driver and carrying help for heavier goods.",
        },
        {
          question: "Can the delivery van make multiple stops in Stockholm?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Perfect if you need to pick up from a supplier in Solna and deliver to offices in the city center and Nacka.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Delivery van in Stockholm in 3 steps",
        step2Text:
          "Our driver picks up your goods in Stockholm, loads the van and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived. The driver has carried everything in and placed it at the right address in Stockholm – the job is done.",
        ctaHeading: "Book a delivery van in Stockholm today",
        ctaText:
          "Describe what you want to transport, enter pickup and delivery addresses in Stockholm. You see the price instantly and can choose express delivery.",
      },
    },
  },

  {
    // Cykelbud Stockholm — config för /cykelbud-stockholm
    // Stadsspecifik sida för cykelbud i Stockholm.
    slug: "cykelbud-stockholm",
    serviceCategory: "budtjänst",
    city: "stockholm",

    meta: {
      title:
        "Cykelbud Stockholm – Miljövänlig Leverans Samma Dag | Ungdomskraft",
      description:
        "Boka cykelbud i Stockholm med leverans samma dag. Nollutsläpp, snabbast genom Stockholms innerstad. Dokument, nycklar, blommor och småpaket. Expressbud inom 30 min.",
      h1: "Cykelbud Stockholm",
    },

    hero: {
      heading: "Grönt och snabbt cykelbud Stockholm",
      intro:
        "Behöver du skicka ett dokument, nycklar eller ett litet paket i Stockholm? Vårt cykelbud tar sig snabbast genom innerstaden – från Norrmalm till Södermalm på minuter, helt utan utsläpp. Expressleverans inom 30 minuter mellan Stockholms stadsdelar.",
      heroImage: "/hero-cykelbud.png",
      heroImageAlt:
        "Ungdomskraft cykelbud Stockholm – ung cykelkurir levererar paket snabbt i Stockholm",
    },

    featureCards: {
      headingText: "Ditt gröna bud i Stockholm",
      sinceText: "Cykelbud i Stockholm sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft skickat cykelbud i Stockholms innerstad och närförorter. Våra unga bud känner varje cykelbana, genväg och portkod – snabbare än bilen, bättre för miljön och med den energi som bara unga Stockholmare kan erbjuda.",
      bookYourText: "Boka ditt cykelbud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Stockholm. Du ser priset direkt – enkelt och snabbt.",
      vehicleTitleText: "Cykel och elmoped – nollutsläpp i Stockholm",
      vehicleDescText:
        "Våra cykelbud kör med cykel eller el-driven moped. Perfekt för Stockholms innerstad där bilar fastnar på Sveavägen och Hornsgatan. Inget bränsle, inga utsläpp, inga parkeringsproblem.",
    },

    itemsSection: {
      heading: "Vanliga cykelbud-leveranser i Stockholm",
      items: [
        {
          name: "Juridiska dokument",
          description:
            "Avtal, kontrakt och handlingar mellan advokatbyråer på Strandvägen, Birger Jarlsgatan och Norrmalmstorg",
          image: "/copy-machine.webp",
        },
        {
          name: "Nycklar & passerkort",
          description:
            "Lägenhetsnycklar till mäklare, kontorsnycklar till nyanställda och passerkort till besökare – snabbt och säkert",
          image: "/toolbox.webp",
        },
        {
          name: "Blommor & presenter",
          description:
            "Buketter från blomsterhandlare på Östermalm, presenter och tårtor som ska fram samma dag i Stockholm",
          image: "/plant.webp",
        },
        {
          name: "Medicin & labbprover",
          description:
            "Recept från vårdcentraler, labbprover till Karolinska och tidskritiska medicinska försändelser i Stockholm",
          image: "/mini-fridge.webp",
        },
        {
          name: "E-handelsreturer",
          description:
            "Returpaket, provkollektioner och expressleveranser till e-handlare och butiker i Stockholms innerstad",
          image: "/box.webp",
        },
        {
          name: "Restaurangleveranser",
          description:
            "Luncher, catering-prover och specialkaffe från restauranger och caféer på Södermalm och Östermalm",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Cykelbud i Stockholm – snabbast genom innerstaden",
      mainText:
        "Stockholms innerstad är som gjord för cykelbud. Smala gator, enkelriktningar, biltullar och kronisk parkeringsbrist gör bilen till det långsammaste sättet att leverera – medan cykeln tar den rakaste vägen. Ungdomskrafts cykelbud tar sig från Norrmalm till Södermalm på under tio minuter, från Vasastan till Östermalm på fem och från Kungsholmen till Gamla stan utan att stanna en enda gång i kö.\n\nVåra cykelbud är unga Stockholmare som känner staden utan och innan. De vet att Pontonjärgatan är en genväg till Kungsholmen, att cykelbanorna längs Strandvägen sparar fem minuter jämfört med Valhallavägen och att portarna på Södermalms bakgator ofta öppnas med sifferkod 1234. Den lokalkännedomen gör att vi levererar snabbare än alla GPS-navigerade bilbud. Vi levererar dokument, nycklar, blommor, medicin, småpaket och andra mindre försändelser samma dag – ofta inom en timme. Varje leverans görs med cykel eller el-driven moped, vilket innebär noll koldioxidutsläpp mitt i Stockholms trafikkaos. För advokatbyråer vid Stureplan, mäklarkontor på Östermalm, vårdcentraler på Kungsholmen och kontorshotell i City är vårt cykelbud det snabbaste och grönaste alternativet.",
      preparationHeading: "Förbereda ditt cykelbud i Stockholm",
      preparationText:
        "Se till att din försändelse är färdigpackad och redo vid hämtning. Dokument bör ligga i kuvert eller mapp. Ömtåliga föremål som blommor eller tårta bör vara ordentligt förpackade. Ange portkod om hämtadressen har en – det är vanligt i Stockholms innerstad. Meddela receptionen om du sitter i en kontorsbyggnad. Cykelbud i Stockholm passar försändelser upp till cirka 10 kg som ryms i en cykelväska. För tyngre eller större gods rekommenderar vi istället vårt bud med bil eller skåpbil.",
      additionalSections: [
        {
          heading: "Stadsdelar vi kör cykelbud i Stockholm",
          content:
            "Vårt huvudsakliga leveransområde för cykelbud i Stockholm är innerstaden och de närmaste förorterna. I Norrmalm, Vasastan, Östermalm, Södermalm och Kungsholmen har vi cykelbud redo dagligen med responstider på 15–30 minuter. Vi täcker även Gamla stan, Djurgården och Hammarby Sjöstad. I närförorterna – Liljeholmen, Gröndal, Aspudden, Enskede, Hammarbyhöjden – kör vi regelbundet men med något längre leveranstid. För leveranser till ytterförort och kranskommuner rekommenderar vi istället vårt vanliga bud med bil eller elmoped, som täcker hela Stockholmsregionen.",
        },
        {
          heading: "Expresscykelbud i Stockholms innerstad",
          content:
            "Stockholms innerstad är vårt snabbaste leveransområde för cykelbud. Med expressleverans prioriteras ditt uppdrag direkt och budet cyklar raka vägen från hämtadress till leveransadress utan omvägar. I innerstaden innebär det normalt 15–30 minuter dörr till dörr. Expressbudet är perfekt för tidskritiska dokument mellan advokatbyråer vid Stureplan och Norrmalmstorg, nycklar som ska till en visning på Östermalm inom kvarten, labbprover från en vårdcentral på Kungsholmen till Karolinska, eller ett kontrakt som måste signeras på Södermalm innan klockan tre. Våra bud navigerar Stockholms cykelbanor snabbare än någon bil i rusningstrafik.",
        },
        {
          heading: "Cykelbud för Stockholmsföretag",
          content:
            "Många företag i Stockholms innerstad har dagliga leveransbehov som passar perfekt för cykelbud – dokument till kunder, nycklar till hyresgäster, prover till labb, paket till kontoret. Istället för att skicka en anställd med bil genom Stockholmstrafiken kan du anlita vårt cykelbud. Det är snabbare, billigare och stärker ert hållbarhetsarbete. Vi erbjuder företagsavtal med rabatterade priser, schemalagda hämtningar och dedikerade cykelbud som lär känna ert kontor. Advokatbyråer, mäklarfirmor, vårdcentraler, arkitektkontor och kontorshotell i Stockholm – alla kan dra nytta av ett grönt budavtal. Vi utfärdar gärna nollutsläppsintyg för er hållbarhetsredovisning.",
        },
        {
          heading: "Varför boka cykelbud i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Snabbast i innerstaden –",
              text: "Cykeln slår bilen i Stockholms innerstad varje gång. Inga köer, inga parkeringsproblem, inga omvägar genom enkelriktade gator.",
            },
            {
              label: "Lokal Stockholmskunskap –",
              text: "Våra bud känner cykelbanor, genvägar och portkoder i varje stadsdel. Lokalkännedom som ingen GPS kan ersätta.",
            },
            {
              label: "Nollutsläpp –",
              text: "Cykel eller elmoped laddad med grön el. Inget avgasrör, inget buller, inga partiklar mitt i Stockholms innerstad.",
            },
            {
              label: "Express inom 30 min –",
              text: "Expresscykelbud i Stockholms innerstad levererar normalt inom 15–30 minuter. Perfekt för tidskritiska dokument och nycklar.",
            },
            {
              label: "Stödjer unga Stockholmare –",
              text: "Varje cykelbud du bokar ger en ung Stockholmare möjlighet att jobba och växa. Budtjänst med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur snabbt levererar cykelbud i Stockholm?",
        answer:
          "I Stockholms innerstad levererar cykelbud vanligtvis inom 1–2 timmar. Med expressleverans kan vi ofta vara på plats inom 15–30 minuter mellan stadsdelar som Norrmalm, Södermalm, Östermalm och Kungsholmen.",
      },
      {
        question: "Vad kostar cykelbud i Stockholm?",
        answer:
          "Cykelbud i Stockholm börjar från 299 kr. Priset beror på avstånd och tid. Du ser exakt pris vid bokning – inga dolda kostnader.",
      },
      {
        question: "Vilka stadsdelar kör ni cykelbud i Stockholm?",
        answer:
          "Vi kör cykelbud i hela Stockholms innerstad – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen, Gamla stan och Djurgården. Vi täcker även närförorter som Liljeholmen, Aspudden, Hammarby Sjöstad och Enskede.",
      },
      {
        question: "Vad kan jag skicka med cykelbud i Stockholm?",
        answer:
          "Dokument, nycklar, småpaket, blommor, medicin, labbprover, mat och andra försändelser som ryms i en cykelväska – upp till cirka 10 kg.",
      },
      {
        question: "Kan ni köra expresscykelbud i Stockholms innerstad?",
        answer:
          "Ja, expresscykelbud i innerstaden levereras normalt inom 15–30 minuter. Perfekt för tidskritiska dokument, nycklar och medicin mellan Stockholms centrala stadsdelar.",
      },
      {
        question: "Är cykelbud i Stockholm verkligen utsläppsfritt?",
        answer:
          "Ja, våra cykelbud kör med cykel eller el-driven moped laddad med grön el. Inga fossila bränslen, inga avgaser, inga partiklar – mitt i Stockholms innerstad.",
      },
      {
        question: "Kan cykelbud leverera till kontor i Stockholms city?",
        answer:
          "Ja, vi levererar dagligen till kontorsbyggnader i Stockholms city, på Stureplan, Norrmalmstorg, Birger Jarlsgatan och övriga innerstaden. Våra bud känner portar och receptioner.",
      },
      {
        question: "Kan cykelbud leverera i regn och snö i Stockholm?",
        answer:
          "Ja, våra Stockholmsbud kör i alla väder. Försändelsen skyddas i vattentät väska och levereras torr och hel oavsett Stockholmsvädret.",
      },
      {
        question: "Kan jag boka cykelbud i Stockholm samma dag?",
        answer:
          "Ja, de flesta cykelbud-uppdrag i Stockholm hämtas och levereras samma dag. Boka så tidigt som möjligt för bästa tillgänglighet.",
      },
      {
        question: "Erbjuder ni cykelbud-avtal för företag i Stockholm?",
        answer:
          "Ja, vi erbjuder företagsavtal för Stockholmsföretag med rabatterade priser, fasta hämtningstider och samlad fakturering. Vi utfärdar även nollutsläppsintyg för hållbarhetsredovisning.",
      },
      {
        question: "Kan cykelbud leverera blommor i Stockholm?",
        answer:
          "Ja, vi levererar buketter och blommor varsamt i speciella väskor. Populärt från blomsterhandlare på Östermalm och Södermalm till mottagare i hela innerstaden.",
      },
      {
        question: "Kan cykelbud leverera labbprover i Stockholm?",
        answer:
          "Ja, vi kör labbprover mellan vårdcentraler och sjukhus i Stockholm, inklusive Karolinska. Tidskritiska medicinska försändelser prioriteras alltid.",
      },
      {
        question: "Kör ni cykelbud på kvällar och helger i Stockholm?",
        answer:
          "Ja, vi erbjuder cykelbud i Stockholm även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider vid bokning.",
      },
      {
        question: "Är mina saker försäkrade under cykelbudet i Stockholm?",
        answer:
          "Ja, alla cykelbud-leveranser i Stockholm är försäkrade under transporten. Kontakta oss i förväg vid särskilt värdefulla föremål.",
      },
      {
        question: "Vad är skillnaden mellan cykelbud och vanligt bud i Stockholm?",
        answer:
          "Cykelbud kör med cykel eller elmoped och passar mindre försändelser i innerstaden. Vanligt bud använder bil eller skåpbil och täcker hela Stockholmsregionen med tyngre gods.",
      },
      {
        question: "Varför är cykelbud snabbare än bilbud i Stockholm?",
        answer:
          "Cykeln tar den rakaste vägen, slipper köer på Sveavägen och Hornsgatan, behöver inte leta parkering och kan använda cykelbanor som bilen inte har tillgång till. I Stockholms innerstad är cykelbud ofta 20–40 minuter snabbare.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Cykelbud i Stockholm i 3 steg",
      step2Text:
        "Vårt cykelbud hämtar din försändelse i Stockholm och cyklar raka vägen till mottagaren. Du får en bekräftelse när leveransen är klar.",
      step3Title: "Levererat",
      step3Desc:
        "Din försändelse har anlänt – snabbt, grönt och utan utsläpp genom Stockholms innerstad.",
      ctaHeading: "Boka cykelbud i Stockholm idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Stockholm. Du ser priset direkt – snabbt, enkelt och miljövänligt.",
    },

    en: {
      slug: "bicycle-courier-stockholm",
      meta: {
        title:
          "Bicycle Courier Stockholm – Eco-Friendly Same Day Delivery | Ungdomskraft",
        description:
          "Book a bicycle courier in Stockholm with same-day delivery. Zero emissions, fastest through the city center. Documents, keys, flowers and small parcels. Express within 30 min.",
        h1: "Bicycle Courier Stockholm",
      },

      hero: {
        heading: "Green and fast bicycle courier Stockholm",
        intro:
          "Need to send a document, keys or a small parcel in Stockholm? Our bicycle courier is the fastest way through the city center – from Norrmalm to Södermalm in minutes, with zero emissions. Express delivery within 30 minutes between Stockholm's neighborhoods.",
        heroImageAlt:
          "Ungdomskraft bicycle courier Stockholm – young bike courier delivering packages fast in Stockholm",
      },

      featureCards: {
        headingText: "Your green courier in Stockholm",
        sinceText: "Bicycle courier in Stockholm since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been sending bicycle couriers across Stockholm's city center and nearby suburbs. Our young couriers know every cycle path, shortcut and entry code – faster than a car, better for the environment and with the energy only young Stockholmers can offer.",
        bookYourText: "Book your bicycle courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses in Stockholm. You see the price instantly – simple and fast.",
        vehicleTitleText: "Bicycle and electric moped – zero emissions in Stockholm",
        vehicleDescText:
          "Our bicycle couriers ride bikes or electric mopeds. Perfect for Stockholm's city center where cars get stuck on Sveavägen and Hornsgatan. No fuel, no emissions, no parking problems.",
      },

      itemsSection: {
        heading: "Common bicycle courier deliveries in Stockholm",
        items: [
          {
            name: "Legal documents",
            description:
              "Agreements, contracts and legal papers between law firms on Strandvägen, Birger Jarlsgatan and Norrmalmstorg",
            image: "/copy-machine.webp",
          },
          {
            name: "Keys & access cards",
            description:
              "Apartment keys to real estate agents, office keys to new employees and access cards to visitors – fast and secure",
            image: "/toolbox.webp",
          },
          {
            name: "Flowers & gifts",
            description:
              "Bouquets from florists on Östermalm, gifts and cakes that need to arrive same day in Stockholm",
            image: "/plant.webp",
          },
          {
            name: "Medicine & lab samples",
            description:
              "Prescriptions from health clinics, lab samples to Karolinska and time-critical medical shipments in Stockholm",
            image: "/mini-fridge.webp",
          },
          {
            name: "E-commerce returns",
            description:
              "Return packages, sample collections and express deliveries to online retailers and stores in central Stockholm",
            image: "/box.webp",
          },
          {
            name: "Restaurant deliveries",
            description:
              "Lunches, catering samples and specialty coffee from restaurants and cafés on Södermalm and Östermalm",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Bicycle courier in Stockholm – fastest through the city center",
        mainText:
          "Stockholm's city center is made for bicycle couriers. Narrow streets, one-way systems, congestion charges and chronic parking shortages make the car the slowest way to deliver – while the bicycle takes the most direct route. Ungdomskraft's bicycle couriers go from Norrmalm to Södermalm in under ten minutes, from Vasastan to Östermalm in five, and from Kungsholmen to Gamla Stan without stopping in traffic once.\n\nOur bicycle couriers are young Stockholmers who know the city inside and out. They know that Pontonjärgatan is a shortcut to Kungsholmen, that the cycle paths along Strandvägen save five minutes compared to Valhallavägen, and that the doors on Södermalm's back streets often open with the code 1234. That local knowledge means we deliver faster than any GPS-navigated car courier. We deliver documents, keys, flowers, medicine, small parcels and other smaller shipments same day – often within one hour. Every delivery is made by bicycle or electric moped, meaning zero carbon emissions right in the middle of Stockholm's traffic chaos. For law firms at Stureplan, real estate offices on Östermalm, health clinics on Kungsholmen and co-working spaces in the City, our bicycle courier is the fastest and greenest option.",
        preparationHeading: "Preparing your bicycle courier in Stockholm",
        preparationText:
          "Make sure your shipment is packed and ready at pickup. Documents should be in an envelope or folder. Fragile items like flowers or cakes should be properly packaged. Provide an entry code if the pickup address has one – this is common in central Stockholm. Let the reception know if you are in an office building. Bicycle courier in Stockholm is suitable for shipments up to approximately 10 kg that fit in a courier bag. For heavier or larger goods, we recommend our regular courier with car or van instead.",
        additionalSections: [
          {
            heading: "Neighborhoods we cover by bicycle in Stockholm",
            content:
              "Our main delivery area for bicycle couriers in Stockholm is the city center and nearest suburbs. In Norrmalm, Vasastan, Östermalm, Södermalm and Kungsholmen we have bicycle couriers ready daily with response times of 15–30 minutes. We also cover Gamla Stan, Djurgården and Hammarby Sjöstad. In the nearby suburbs – Liljeholmen, Gröndal, Aspudden, Enskede, Hammarbyhöjden – we ride regularly but with slightly longer delivery times. For deliveries to outer suburbs and surrounding municipalities, we recommend our regular courier with car or electric moped, which covers the entire Stockholm region.",
          },
          {
            heading: "Express bicycle courier in central Stockholm",
            content:
              "Stockholm's city center is our fastest delivery zone for bicycle couriers. With express delivery, your job is prioritized immediately and the courier cycles straight from pickup to delivery without detours. In the city center, this normally means 15–30 minutes door to door. The express courier is perfect for time-critical documents between law firms at Stureplan and Norrmalmstorg, keys that need to reach a showing on Östermalm within fifteen minutes, lab samples from a health clinic on Kungsholmen to Karolinska, or a contract that needs signing on Södermalm before three o'clock. Our couriers navigate Stockholm's cycle paths faster than any car in rush hour traffic.",
          },
          {
            heading: "Bicycle courier for Stockholm businesses",
            content:
              "Many businesses in central Stockholm have daily delivery needs that are a perfect fit for bicycle couriers – documents to clients, keys to tenants, samples to labs, packages to the office. Instead of sending an employee by car through Stockholm traffic, you can use our bicycle courier. It's faster, cheaper and strengthens your sustainability efforts. We offer business agreements with discounted rates, scheduled pickups and dedicated bicycle couriers who get to know your office. Law firms, real estate agencies, health clinics, architecture studios and co-working spaces in Stockholm – all can benefit from a green courier agreement. We're happy to issue zero-emission certificates for your sustainability reporting.",
          },
          {
            heading: "Why book a bicycle courier in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Fastest in the city center –",
                text: "The bicycle beats the car in Stockholm's inner city every time. No queues, no parking problems, no detours through one-way streets.",
              },
              {
                label: "Local Stockholm knowledge –",
                text: "Our couriers know cycle paths, shortcuts and entry codes in every neighborhood. Local knowledge that no GPS can replace.",
              },
              {
                label: "Zero emissions –",
                text: "Bicycle or electric moped charged with green electricity. No exhaust, no noise, no particulates in central Stockholm.",
              },
              {
                label: "Express within 30 min –",
                text: "Express bicycle courier in Stockholm's city center delivers within 15–30 minutes. Perfect for time-critical documents and keys.",
              },
              {
                label: "Supporting young Stockholmers –",
                text: "Every bicycle courier you book gives a young Stockholmer the opportunity to work and grow. Courier service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How fast does a bicycle courier deliver in Stockholm?",
          answer:
            "In Stockholm's city center, bicycle couriers typically deliver within 1–2 hours. With express delivery, we can often arrive within 15–30 minutes between neighborhoods like Norrmalm, Södermalm, Östermalm and Kungsholmen.",
        },
        {
          question: "How much does a bicycle courier in Stockholm cost?",
          answer:
            "Bicycle courier in Stockholm starts from SEK 299. The price depends on distance and time. You see the exact price when booking – no hidden costs.",
        },
        {
          question: "Which Stockholm neighborhoods do you cover by bicycle?",
          answer:
            "We cover all of Stockholm's city center – Norrmalm, Vasastan, Östermalm, Södermalm, Kungsholmen, Gamla Stan and Djurgården. We also cover nearby suburbs like Liljeholmen, Aspudden, Hammarby Sjöstad and Enskede.",
        },
        {
          question: "What can I send by bicycle courier in Stockholm?",
          answer:
            "Documents, keys, small parcels, flowers, medicine, lab samples, food and other shipments that fit in a courier bag – up to approximately 10 kg.",
        },
        {
          question: "Can you do express bicycle courier in central Stockholm?",
          answer:
            "Yes, express bicycle courier in the city center delivers within 15–30 minutes. Perfect for time-critical documents, keys and medicine between Stockholm's central neighborhoods.",
        },
        {
          question: "Is bicycle courier in Stockholm really zero-emission?",
          answer:
            "Yes, our bicycle couriers ride bikes or electric mopeds charged with green electricity. No fossil fuels, no exhaust, no particulates – right in central Stockholm.",
        },
        {
          question: "Can bicycle couriers deliver to offices in Stockholm City?",
          answer:
            "Yes, we deliver daily to office buildings in Stockholm City, at Stureplan, Norrmalmstorg, Birger Jarlsgatan and across the city center. Our couriers know the doors and receptions.",
        },
        {
          question: "Can bicycle couriers deliver in rain and snow in Stockholm?",
          answer:
            "Yes, our Stockholm couriers ride in all weather. The shipment is protected in a waterproof bag and delivered dry and intact regardless of Stockholm's weather.",
        },
        {
          question: "Can I book a bicycle courier in Stockholm the same day?",
          answer:
            "Yes, most bicycle courier jobs in Stockholm are picked up and delivered the same day. Book as early as possible for best availability.",
        },
        {
          question: "Do you offer bicycle courier contracts for Stockholm businesses?",
          answer:
            "Yes, we offer business agreements for Stockholm companies with discounted rates, scheduled pickups and consolidated invoicing. We also issue zero-emission certificates for sustainability reporting.",
        },
        {
          question: "Can bicycle couriers deliver flowers in Stockholm?",
          answer:
            "Yes, we deliver bouquets and flowers carefully in special bags. Popular from florists on Östermalm and Södermalm to recipients across the city center.",
        },
        {
          question: "Can bicycle couriers deliver lab samples in Stockholm?",
          answer:
            "Yes, we deliver lab samples between health clinics and hospitals in Stockholm, including Karolinska. Time-critical medical shipments are always prioritized.",
        },
        {
          question: "Do you offer bicycle courier on evenings and weekends in Stockholm?",
          answer:
            "Yes, we offer bicycle courier in Stockholm on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "Are my items insured during bicycle courier in Stockholm?",
          answer:
            "Yes, all bicycle courier deliveries in Stockholm are insured during transport. Contact us in advance for particularly valuable items.",
        },
        {
          question: "What is the difference between bicycle courier and regular courier in Stockholm?",
          answer:
            "Bicycle courier uses bikes or electric mopeds for smaller shipments in the city center. Regular courier uses cars or vans and covers the entire Stockholm region with heavier goods.",
        },
        {
          question: "Why is bicycle courier faster than car courier in Stockholm?",
          answer:
            "The bicycle takes the most direct route, avoids queues on Sveavägen and Hornsgatan, doesn't need to search for parking and can use cycle paths that cars can't access. In Stockholm's city center, bicycle courier is often 20–40 minutes faster.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Bicycle courier in Stockholm in 3 steps",
        step2Text:
          "Our bicycle courier picks up your shipment in Stockholm and cycles straight to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your shipment has arrived – fast, green and with zero emissions through Stockholm's city center.",
        ctaHeading: "Book a bicycle courier in Stockholm today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses in Stockholm. You see the price instantly – fast, simple and eco-friendly.",
      },
    },
  },
];
